import React, { useEffect, useState } from 'react';
import Header from '../../components/ui/Header';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { supabase } from '../../utils/supabaseClient';

const AdminDashboard = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [complaints, setComplaints] = useState([]);
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: ''
  });
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusUpdateData, setStatusUpdateData] = useState({
    complaintId: null,
    newStatus: '',
    details: ''
  });
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);

  // เพิ่ม state สำหรับแท็บ admin
  const [adminTab, setAdminTab] = useState('complaints'); // 'complaints' | 'news' | 'org' | 'media' | 'documents' | 'procurement' | 'appointments'

  // --- ข่าวประชาสัมพันธ์ ---
  const [newsList, setNewsList] = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const [newsError, setNewsError] = useState(null);
  const [showNewsForm, setShowNewsForm] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [newsForm, setNewsForm] = useState({
    title: '',
    content: '',
    publish_date: '',
    category: '',
    image_url: '',
    media_urls: [], // สำหรับเก็บหลายไฟล์ (รูปภาพและวีดีโอ)
  });
  const [newsMediaFiles, setNewsMediaFiles] = useState([]); // เก็บไฟล์ที่เลือก (รูปภาพและวีดีโอ)
  const [newsFormLoading, setNewsFormLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [previewUrls, setPreviewUrls] = useState([]); // เก็บ ObjectURLs สำหรับ preview
  
  // สร้างและ cleanup preview URLs เมื่อมีไฟล์ใหม่
  useEffect(() => {
    if (newsMediaFiles.length === 0) {
      setPreviewUrls([]);
      return;
    }
    
    const urls = newsMediaFiles.map(file => URL.createObjectURL(file));
    setPreviewUrls(urls);
    
    return () => {
      urls.forEach(url => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          // Ignore errors when revoking URLs
        }
      });
    };
  }, [newsMediaFiles]);
  // Pagination: News
  const [newsPage, setNewsPage] = useState(1);
  const [newsPageSize, setNewsPageSize] = useState(5);
  const newsTotalPages = Math.max(1, Math.ceil((newsList?.length || 0) / newsPageSize));
  const newsPaged = React.useMemo(() => {
    const start = (newsPage - 1) * newsPageSize;
    return (newsList || []).slice(start, start + newsPageSize);
  }, [newsList, newsPage, newsPageSize]);
  useEffect(() => { setNewsPage(1); }, [newsList, newsPageSize]);

  // --- โครงสร้างองค์กร ---
  const [orgList, setOrgList] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgError, setOrgError] = useState(null);
  const [showOrgForm, setShowOrgForm] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [orgForm, setOrgForm] = useState({ name: '', position: '', phone: '', image_url: '' });
  const [orgImageFile, setOrgImageFile] = useState(null);
  const [orgFormLoading, setOrgFormLoading] = useState(false);
  // Pagination for org list
  const [orgPage, setOrgPage] = useState(1);
  const [orgPageSize, setOrgPageSize] = useState(5); // default 5 per page
  const orgTotalPages = Math.max(1, Math.ceil((orgList?.length || 0) / orgPageSize));
  const orgPaged = React.useMemo(() => {
    const start = (orgPage - 1) * orgPageSize;
    return (orgList || []).slice(start, start + orgPageSize);
  }, [orgList, orgPage, orgPageSize]);
  useEffect(() => { setOrgPage(1); }, [orgList, orgPageSize]);
  // Scroll to form when opened
  const orgFormRef = React.useRef(null);
  useEffect(() => {
    if (showOrgForm && orgFormRef.current) {
      try { orgFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch {}
    }
  }, [showOrgForm]);

  // --- Departments (สำหรับชื่อหน่วยงาน/หัวหน้า/ตำแหน่งหัวหน้า แสดงทุกมุมมอง) ---
  const [deptList, setDeptList] = useState([]);
  const [deptLoading, setDeptLoading] = useState(false);
  const [deptError, setDeptError] = useState(null);
  const [showDeptForm, setShowDeptForm] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [deptForm, setDeptForm] = useState({ name: '', head: '', head_position: '' });
  const [deptFormLoading, setDeptFormLoading] = useState(false);

  // --- สื่อประชาสัมพันธ์/เอกสาร ---
  const [mediaList, setMediaList] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaError, setMediaError] = useState(null);
  const [showMediaForm, setShowMediaForm] = useState(false);
  const [editingMedia, setEditingMedia] = useState(null);
  const [mediaForm, setMediaForm] = useState({ title: '', date: '', description: '', file_url: '', image_url: '', media_urls: '' });
  const [mediaImageFiles, setMediaImageFiles] = useState([]);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaPreviewUrls, setMediaPreviewUrls] = useState([]);
  const [mediaFormLoading, setMediaFormLoading] = useState(false);
  const [mediaUploadProgress, setMediaUploadProgress] = useState({});

  // --- หนังสือราชการ ---
  const [docList, setDocList] = useState([]);
  const [docLoading, setDocLoading] = useState(false);
  const [docError, setDocError] = useState(null);
  const [showDocForm, setShowDocForm] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [docForm, setDocForm] = useState({ 
    title: '', 
    document_number: '', 
    date: '', 
    category: '', 
    file_url: '', 
    description: '' 
  });
  const [docFile, setDocFile] = useState(null);
  const [docFormLoading, setDocFormLoading] = useState(false);
  // Pagination: Documents
  const [docPage, setDocPage] = useState(1);
  const [docPageSize, setDocPageSize] = useState(5);
  const docTotalPages = Math.max(1, Math.ceil((docList?.length || 0) / docPageSize));
  const docPaged = React.useMemo(() => {
    const start = (docPage - 1) * docPageSize;
    return (docList || []).slice(start, start + docPageSize);
  }, [docList, docPage, docPageSize]);
  useEffect(() => { setDocPage(1); }, [docList, docPageSize]);

  // --- จัดซื้อจัดจ้าง ---
  const [procList, setProcList] = useState([]);
  const [procLoading, setProcLoading] = useState(false);
  const [procError, setProcError] = useState(null);
  const [showProcForm, setShowProcForm] = useState(false);
  const [editingProc, setEditingProc] = useState(null);
  const [procForm, setProcForm] = useState({
    title: '',
    summary: '',
    publish_date: '',
    category: 'จัดซื้อ', // จัดซื้อ | แผน | สัญญา
    method: '',
    budget: '',
    file_url: ''
  });
  const [procFile, setProcFile] = useState(null);
  const [procFiles, setProcFiles] = useState([]); // [{name:'', file:File|null, url:string|null}]
  const [procFormLoading, setProcFormLoading] = useState(false);
  // Pagination: Procurement
  const [procPage, setProcPage] = useState(1);
  const [procPageSize, setProcPageSize] = useState(5);
  const procTotalPages = Math.max(1, Math.ceil((procList?.length || 0) / procPageSize));
  const procPaged = React.useMemo(() => {
    const start = (procPage - 1) * procPageSize;
    return (procList || []).slice(start, start + procPageSize);
  }, [procList, procPage, procPageSize]);
  useEffect(() => { setProcPage(1); }, [procList, procPageSize]);

  // --- การจองคิวออนไลน์ ---
  const [appointments, setAppointments] = useState([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [appointmentsError, setAppointmentsError] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showAppointmentDetail, setShowAppointmentDetail] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleForm, setRescheduleForm] = useState({
    appointment_date: '',
    time_slot: ''
  });
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [bookedSlots, setBookedSlots] = useState([]); // เวลาที่ถูกจองแล้วในวันที่เลือก
  const [appointmentStatusFilter, setAppointmentStatusFilter] = useState('all'); // 'all' | 'confirmed' | 'cancelled' | 'completed'
  
  // Time slots สำหรับเลือกเวลา
  const timeSlots = [
    { value: '09:00-10:00', label: '09:00 - 10:00' },
    { value: '10:00-11:00', label: '10:00 - 11:00' },
    { value: '11:00-12:00', label: '11:00 - 12:00' },
    { value: '13:00-14:00', label: '13:00 - 14:00' },
    { value: '14:00-15:00', label: '14:00 - 15:00' }
  ];

  // ดึงเวลาที่ถูกจองแล้วเมื่อเลือกวันที่
  const fetchBookedSlots = async (date, currentAppointmentId = null) => {
    if (!date) {
      setBookedSlots([]);
      return;
    }
    
    try {
      let query = supabase
        .from('appointments')
        .select('time_slot, id')
        .eq('appointment_date', date)
        .eq('status', 'confirmed');
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      // กรองเวลาที่ถูกจองแล้ว โดยไม่นับเวลาของการจองคิวปัจจุบัน (ถ้ามี)
      const slots = (data || [])
        .filter(item => !currentAppointmentId || item.id !== currentAppointmentId)
        .map(item => item.time_slot);
      
      setBookedSlots(slots);
    } catch (error) {
      console.error('Error fetching booked slots:', error);
      setBookedSlots([]);
    }
  };
  // Pagination: Appointments
  const [appointmentsPage, setAppointmentsPage] = useState(1);
  const [appointmentsPageSize, setAppointmentsPageSize] = useState(10);
  const appointmentsTotalPages = Math.max(1, Math.ceil((appointments?.length || 0) / appointmentsPageSize));
  const appointmentsPaged = React.useMemo(() => {
    const start = (appointmentsPage - 1) * appointmentsPageSize;
    return (appointments || []).slice(start, start + appointmentsPageSize);
  }, [appointments, appointmentsPage, appointmentsPageSize]);
  useEffect(() => { setAppointmentsPage(1); }, [appointments, appointmentsPageSize]);

  // Check if user is logged in
  useEffect(() => {
    const checkUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      console.log('Current user:', user);
      setUser(user);
      setLoading(false);
    };
    
    checkUser();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user || null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Load complaints when user is logged in
  useEffect(() => {
    if (user) {
      loadComplaints();
    }
  }, [user]);

  // โหลดรายการจัดซื้อจัดจ้าง
  const loadProcList = async () => {
    setProcLoading(true);
    setProcError(null);
    const { data, error } = await supabase
      .from('procurements')
      .select('*')
      .order('publish_date', { ascending: false });
    if (error) setProcError(error.message);
    setProcList(data || []);
    setProcLoading(false);
  };
  useEffect(() => {
    if (user && adminTab === 'procurement') loadProcList();
  }, [user, adminTab]);

  // โหลดรายการจองคิว
  const loadAppointments = async () => {
    setAppointmentsLoading(true);
    setAppointmentsError(null);
    try {
      const { data, error } = await supabase
        .from('appointments')
        .select('*')
        .order('appointment_date', { ascending: false })
        .order('time_slot', { ascending: true });
      
      if (error) throw error;
      
      // กรองตามสถานะ
      let filtered = data || [];
      if (appointmentStatusFilter !== 'all') {
        filtered = filtered.filter(apt => apt.status === appointmentStatusFilter);
      }
      
      setAppointments(filtered);
    } catch (error) {
      console.error('Error loading appointments:', error);
      setAppointmentsError(error.message);
      setAppointments([]);
    } finally {
      setAppointmentsLoading(false);
    }
  };

  useEffect(() => {
    if (user && adminTab === 'appointments') {
      loadAppointments();
    }
  }, [user, adminTab, appointmentStatusFilter]);

  // อัปเดตสถานะการจองคิว
  const handleUpdateAppointmentStatus = async (appointmentId, newStatus) => {
    try {
      const { error } = await supabase
        .from('appointments')
        .update({ status: newStatus })
        .eq('id', appointmentId);
      
      if (error) throw error;
      
      alert('อัปเดตสถานะสำเร็จ');
      loadAppointments();
    } catch (error) {
      console.error('Error updating appointment status:', error);
      alert('อัปเดตสถานะไม่สำเร็จ: ' + error.message);
    }
  };

  // ลบการจองคิว
  const handleDeleteAppointment = async (appointmentId) => {
    if (!window.confirm('ยืนยันลบการจองคิวนี้?')) return;
    
    try {
      const { error } = await supabase
        .from('appointments')
        .delete()
        .eq('id', appointmentId);
      
      if (error) throw error;
      
      alert('ลบการจองคิวสำเร็จ');
      loadAppointments();
    } catch (error) {
      console.error('Error deleting appointment:', error);
      alert('ลบการจองคิวไม่สำเร็จ: ' + error.message);
    }
  };

  // เปิด modal เลื่อนนัด
  const handleOpenRescheduleModal = async (appointment) => {
    setSelectedAppointment(appointment);
    const dateStr = appointment.appointment_date ? appointment.appointment_date.split('T')[0] : '';
    setRescheduleForm({
      appointment_date: dateStr,
      time_slot: appointment.time_slot || ''
    });
    setShowRescheduleModal(true);
    // ดึงเวลาที่ถูกจองแล้วในวันที่เลือก (ถ้ามี) โดยไม่นับเวลาของการจองคิวปัจจุบัน
    if (dateStr) {
      await fetchBookedSlots(dateStr, appointment.id);
    }
  };

  // เลื่อนนัด
  const handleRescheduleAppointment = async (e) => {
    e.preventDefault();
    if (!selectedAppointment) return;
    
    if (!rescheduleForm.appointment_date || !rescheduleForm.time_slot) {
      alert('กรุณาเลือกวันที่และเวลาที่ต้องการเลื่อนนัด');
      return;
    }

    setRescheduleLoading(true);
    try {
      // ตรวจสอบว่ามีคนจองเวลานั้นแล้วหรือยัง (ไม่นับการจองคิวปัจจุบัน)
      const { data: existingAppointments, error: checkError } = await supabase
        .from('appointments')
        .select('id')
        .eq('appointment_date', rescheduleForm.appointment_date)
        .eq('time_slot', rescheduleForm.time_slot)
        .eq('status', 'confirmed')
        .neq('id', selectedAppointment.id);
      
      if (checkError) throw checkError;
      
      if (existingAppointments && existingAppointments.length > 0) {
        alert('เวลานี้ถูกจองแล้ว กรุณาเลือกเวลาอื่น');
        setRescheduleLoading(false);
        return;
      }

      // อัปเดตการจองคิว
      const { error } = await supabase
        .from('appointments')
        .update({
          appointment_date: rescheduleForm.appointment_date,
          time_slot: rescheduleForm.time_slot
        })
        .eq('id', selectedAppointment.id);
      
      if (error) throw error;
      
      alert('เลื่อนนัดสำเร็จ');
      setShowRescheduleModal(false);
      setSelectedAppointment(null);
      setRescheduleForm({ appointment_date: '', time_slot: '' });
      setBookedSlots([]);
      loadAppointments();
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      alert('เลื่อนนัดไม่สำเร็จ: ' + error.message);
    } finally {
      setRescheduleLoading(false);
    }
  };

  // ดึงข่าวจาก Supabase
  const loadNews = async () => {
    setNewsLoading(true);
    setNewsError(null);
    const { data, error } = await supabase
      .from('news')
      .select('*')
      .order('publish_date', { ascending: false });
    if (error) setNewsError(error.message);
    setNewsList(data || []);
    setNewsLoading(false);
  };
  useEffect(() => {
    if (user && adminTab === 'news') loadNews();
  }, [user, adminTab]);

  const loadComplaints = async () => {
    try {
      const { data, error } = await supabase
        .from('complaints')
        .select(`
          *,
          complaint_events (
            id,
            status,
            description,
            created_at
          )
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading complaints:', error);
        return;
      }

      setComplaints(data || []);
    } catch (error) {
      console.error('Error loading complaints:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: loginForm.email,
        password: loginForm.password,
      });

      if (error) {
        setLoginError('อีเมลหรือรหัสผ่านไม่ถูกต้อง');
        return;
      }

      setUser(data.user);
    } catch (error) {
      setLoginError('เกิดข้อผิดพลาดในการเข้าสู่ระบบ');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  const updateComplaintStatus = async (complaintId, newStatus, details = '') => {
    try {
      const { error } = await supabase
        .from('complaints')
        .update({ status: newStatus })
        .eq('id', complaintId);

      if (error) {
        console.error('Error updating status:', error);
        return;
      }

      // Add event
      await supabase
        .from('complaint_events')
        .insert({
          complaint_id: complaintId,
          status: newStatus,
          description: details || getDefaultStatusDescription(newStatus),
          created_at: new Date().toISOString()
        });

      // Reload complaints
      loadComplaints();

      // LINE notify (best-effort)
      try {
        const complaint = complaints.find(c => c.id === complaintId);
        await supabase.functions.invoke('line-notify', {
          body: {
            type: 'status_update',
            ticket_no: complaint?.ticket_no,
            status: newStatus,
            by: user?.email || 'admin'
          }
        });
      } catch (e) {
        console.warn('line notify failed:', e);
      }
    } catch (error) {
      console.error('Error updating complaint:', error);
    }
  };

  const getDefaultStatusDescription = (status) => {
    switch (status) {
      case 'received':
        return 'ระบบรับเรื่องเรียบร้อย';
      case 'in_progress':
        return 'เริ่มดำเนินการ';
      case 'completed':
        return 'ดำเนินการแล้วเสร็จ';
      default:
        return 'อัปเดตสถานะ';
    }
  };

  const openStatusModal = (complaintId, newStatus) => {
    setStatusUpdateData({
      complaintId,
      newStatus,
      details: ''
    });
    setShowStatusModal(true);
  };

  const handleStatusUpdate = async () => {
    if (!statusUpdateData.details.trim()) {
      alert('กรุณากรอกรายละเอียดการดำเนินการ');
      return;
    }

    await updateComplaintStatus(
      statusUpdateData.complaintId,
      statusUpdateData.newStatus,
      statusUpdateData.details
    );

    setShowStatusModal(false);
    setStatusUpdateData({
      complaintId: null,
      newStatus: '',
      details: ''
    });
  };

  const handleViewDetails = (complaint) => {
    setSelectedComplaint(complaint);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedComplaint(null);
  };

  // ฟังก์ชันเพิ่ม/แก้ไขข่าว
  const handleNewsFormSubmit = async (e) => {
    e.preventDefault();
    setNewsFormLoading(true);
    setUploadProgress({});
    
    try {
      // ตรวจสอบ authentication
      if (!user) {
        alert('กรุณาเข้าสู่ระบบก่อนอัปโหลดไฟล์');
        setNewsFormLoading(false);
        return;
      }
      
      // ตรวจสอบ session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        alert('Session หมดอายุ กรุณาเข้าสู่ระบบใหม่');
        setNewsFormLoading(false);
        return;
      }
      
      console.log('Submitting news form:', newsForm);
      console.log('Current user:', user);
      console.log('Session:', session ? 'Active' : 'None');
      
      let imageUrl = newsForm.image_url;
      let mediaUrls = Array.isArray(newsForm.media_urls) ? [...newsForm.media_urls] : [];
      
      // อัปโหลดไฟล์หลายไฟล์ (รูปภาพและวีดีโอ)
      if (newsMediaFiles && newsMediaFiles.length > 0) {
        const uploadedUrls = [];
        
        for (let i = 0; i < newsMediaFiles.length; i++) {
          const file = newsMediaFiles[i];
          const fileExt = file.name.split('.').pop();
          const fileName = `news_${Date.now()}_${i}.${fileExt}`;
          const isVideo = file.type.startsWith('video/');
          // ใช้ bucket เดียวกันสำหรับรูปภาพและวีดีโอ (news-images)
          // ถ้าต้องการแยก bucket สามารถสร้าง news-videos bucket ใน Supabase ได้
          const bucketName = 'news-images';
          
          console.log(`Uploading ${isVideo ? 'video' : 'image'}:`, fileName);
          setUploadProgress(prev => ({ ...prev, [i]: `กำลังอัปโหลด ${file.name}...` }));
          
          try {
            const { data: uploadData, error: uploadError } = await supabase.storage
              .from(bucketName)
              .upload(fileName, file, {
                cacheControl: '3600',
                upsert: false
              });
            
            if (uploadError) {
              console.error('Upload error:', uploadError);
              console.error('Error details:', {
                message: uploadError.message,
                statusCode: uploadError.statusCode,
                error: uploadError.error
              });
              
              // ตรวจสอบ error types
              if (uploadError.message.includes('row-level security') || uploadError.message.includes('RLS')) {
                throw new Error(`อัปโหลด ${file.name} ไม่สำเร็จ: ข้อผิดพลาดด้านความปลอดภัย\n\nกรุณาตรวจสอบ:\n1. รัน SQL ในไฟล์ storage_policies_fix.sql\n2. ตรวจสอบว่า bucket "news-images" มีอยู่\n3. ตรวจสอบว่า policies ถูกตั้งค่าถูกต้อง`);
              } else if (uploadError.message.includes('Bucket not found') || uploadError.message.includes('not found')) {
                throw new Error(`ไม่พบ storage bucket "${bucketName}"\n\nกรุณาสร้าง bucket ใน Supabase Storage:\n1. ไปที่ Supabase Dashboard → Storage\n2. สร้าง bucket ชื่อ "news-images"\n3. ตั้งค่าเป็น Public bucket\n4. รัน SQL ในไฟล์ storage_policies_fix.sql`);
              } else if (uploadError.message.includes('JWT') || uploadError.message.includes('token')) {
                throw new Error(`อัปโหลด ${file.name} ไม่สำเร็จ: Session หมดอายุ\n\nกรุณาเข้าสู่ระบบใหม่`);
              }
              throw new Error(`อัปโหลด ${file.name} ไม่สำเร็จ: ${uploadError.message}`);
            }
            
            const publicUrl = supabase.storage.from(bucketName).getPublicUrl(fileName).data.publicUrl;
            uploadedUrls.push({
              url: publicUrl,
              type: isVideo ? 'video' : 'image',
              name: file.name
            });
            
            setUploadProgress(prev => ({ ...prev, [i]: `อัปโหลด ${file.name} สำเร็จ` }));
          } catch (error) {
            console.error(`Error uploading file ${i}:`, error);
            setUploadProgress(prev => ({ ...prev, [i]: `เกิดข้อผิดพลาด: ${error.message}` }));
            throw error;
          }
        }
        
        // รวม URLs ที่อัปโหลดใหม่กับ URLs เดิม (ถ้ามี)
        mediaUrls = [...mediaUrls, ...uploadedUrls];
        
        // ถ้ายังไม่มี image_url และมีรูปภาพที่อัปโหลด ให้ใช้รูปภาพแรก
        if (!imageUrl) {
          const firstImage = uploadedUrls.find(m => m.type === 'image');
          if (firstImage) {
            imageUrl = firstImage.url;
          }
        } else if (uploadedUrls.length > 0) {
          // ถ้ามี image_url อยู่แล้ว แต่ต้องการใช้รูปใหม่ที่อัปโหลด
          const firstNewImage = uploadedUrls.find(m => m.type === 'image');
          if (firstNewImage) {
            imageUrl = firstNewImage.url;
          }
        }
      }
      
      // ถ้ายังไม่มี image_url แต่มี media_urls เดิม ให้ใช้รูปภาพแรกจาก media_urls
      if (!imageUrl && mediaUrls.length > 0) {
        const firstImage = mediaUrls.find(m => m.type === 'image');
        if (firstImage) {
          imageUrl = firstImage.url;
        }
      }
      
      // เตรียมข้อมูลสำหรับบันทึก
      const newsData = {
        title: newsForm.title,
        content: newsForm.content,
        publish_date: newsForm.publish_date,
        category: newsForm.category,
        image_url: imageUrl || null,
      };
      
      // เพิ่ม media_urls ถ้ามี (อาจไม่มีคอลัมน์นี้ในตาราง)
      if (mediaUrls.length > 0) {
        try {
          newsData.media_urls = JSON.stringify(mediaUrls);
        } catch (e) {
          console.warn('Could not stringify media_urls:', e);
        }
      }
      
      if (editingNews) {
        // แก้ไขข่าว
        const { error } = await supabase
          .from('news')
          .update(newsData)
          .eq('id', editingNews.id);
        
        if (error) {
          console.error('Update error:', error);
          // ถ้า error เกี่ยวกับคอลัมน์ media_urls ให้แจ้งเตือน
          if (error.message.includes('media_urls') || error.message.includes('column')) {
            throw new Error('แก้ไขข่าวไม่สำเร็จ: ต้องการเพิ่มคอลัมน์ media_urls ในตาราง news (ประเภท TEXT หรือ JSONB)');
          }
          throw new Error('แก้ไขข่าวไม่สำเร็จ: ' + error.message);
        }
        alert('แก้ไขข่าวสำเร็จ');
      } else {
        // เพิ่มข่าวใหม่
        const { error } = await supabase
          .from('news')
          .insert([newsData]);
        
        if (error) {
          console.error('Insert error:', error);
          // ถ้า error เกี่ยวกับคอลัมน์ media_urls ให้แจ้งเตือน
          if (error.message.includes('media_urls') || error.message.includes('column')) {
            throw new Error('เพิ่มข่าวไม่สำเร็จ: ต้องการเพิ่มคอลัมน์ media_urls ในตาราง news (ประเภท TEXT หรือ JSONB)\n\nกรุณารัน SQL:\nALTER TABLE news ADD COLUMN media_urls TEXT;');
          }
          throw new Error('เพิ่มข่าวไม่สำเร็จ: ' + error.message);
        }
        alert('เพิ่มข่าวสำเร็จ');
      }
      
      // รีเซ็ตฟอร์ม
      setShowNewsForm(false);
      setEditingNews(null);
      setNewsForm({ title: '', content: '', publish_date: '', category: '', image_url: '', media_urls: [] });
      setNewsMediaFiles([]);
      setUploadProgress({});
      loadNews();
    } catch (error) {
      console.error('Error in handleNewsFormSubmit:', error);
      alert(error.message || 'เกิดข้อผิดพลาดในการบันทึกข่าว');
    } finally {
      setNewsFormLoading(false);
    }
  };

  const handleEditNews = (news) => {
    setEditingNews(news);
    
    // แปลง media_urls จาก JSON string เป็น array
    let mediaUrls = [];
    if (news.media_urls) {
      try {
        mediaUrls = typeof news.media_urls === 'string' 
          ? JSON.parse(news.media_urls) 
          : (Array.isArray(news.media_urls) ? news.media_urls : []);
      } catch (e) {
        console.error('Error parsing media_urls:', e);
        mediaUrls = [];
      }
    }
    
    setNewsForm({
      title: news.title,
      content: news.content,
      publish_date: news.publish_date?.slice(0, 10),
      category: news.category,
      image_url: news.image_url,
      media_urls: mediaUrls,
    });
    setNewsMediaFiles([]); // รีเซ็ตไฟล์ที่เลือก
    setUploadProgress({});
    setShowNewsForm(true);
  };
  const handleDeleteNews = async (id) => {
    if (!window.confirm('ยืนยันลบข่าวนี้?')) return;
    await supabase.from('news').delete().eq('id', id);
    loadNews();
  };

  // ฟังก์ชันเพิ่ม/แก้ไขบุคลากร (โครงสร้างองค์กร)
  const handleOrgFormSubmit = async (e) => {
    e.preventDefault();
    setOrgFormLoading(true);
    console.log('Submitting org form:', orgForm);
    console.log('Editing org:', editingOrg);
    
    let imageUrl = orgForm.image_url;
    if (orgImageFile) {
      const fileExt = orgImageFile.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      console.log('Uploading image:', fileName);
      const { data: uploadData, error: uploadError } = await supabase.storage.from('org-images').upload(fileName, orgImageFile);
      if (uploadError) {
        console.error('Upload error:', uploadError);
        setOrgFormLoading(false);
        alert('อัปโหลดรูปภาพไม่สำเร็จ');
        return;
      }
      imageUrl = supabase.storage.from('org-images').getPublicUrl(fileName).data.publicUrl;
      console.log('Image URL:', imageUrl);
    }
    if (editingOrg) {
      // Remove id from update data since it's GENERATED ALWAYS
      const { id, ...updateData } = orgForm;
      console.log('Updating org with data:', { ...updateData, image_url: imageUrl });
      const { error } = await supabase
        .from('org_structure')
        .update({ ...updateData, image_url: imageUrl })
        .eq('id', editingOrg.id);
      if (error) {
        console.error('Update error:', error);
        alert('แก้ไขบุคลากรไม่สำเร็จ');
      }
    } else {
      const { error } = await supabase
        .from('org_structure')
        .insert([{ ...orgForm, image_url: imageUrl }]);
      if (error) alert('เพิ่มบุคลากรไม่สำเร็จ');
    }
    setShowOrgForm(false);
    setEditingOrg(null);
    setOrgForm({ name: '', position: '', phone: '', image_url: '' });
    setOrgImageFile(null);
    setOrgFormLoading(false);
    loadOrgList(); // Reload org list after submission
    // Notify public page to reload if open (simple event)
    try { window.dispatchEvent(new Event('org-data-updated')); } catch {}
  };

  const handleEditOrg = (person) => {
    setEditingOrg(person);
    // Don't include id in form data since it's GENERATED ALWAYS
    const { id, ...formData } = person;
    setOrgForm({
      name: formData.name,
      position: formData.position,
      phone: formData.phone,
      image_url: formData.image_url,
    });
    setShowOrgForm(true);
  };
  const handleDeleteOrg = async (id) => {
    if (!window.confirm('ยืนยันลบบุคลากรนี้?')) return;
    await supabase.from('org_structure').delete().eq('id', id);
    loadOrgList();
    try { window.dispatchEvent(new Event('org-data-updated')); } catch {}
  };

  // Reset image to use local folder by clearing image_url
  const handleResetOrgImage = async (person) => {
    if (!person?.id) return;
    if (!window.confirm(`ยืนยันคืนค่าให้ใช้รูปจากโฟลเดอร์สำหรับ\n${person.name}?`)) return;
    const { error } = await supabase
      .from('org_structure')
      .update({ image_url: null })
      .eq('id', person.id);
    if (error) { alert('รีเซ็ตรูปไม่สำเร็จ'); return; }
    await loadOrgList();
    try { window.dispatchEvent(new Event('org-data-updated')); } catch {}
  };

  const loadOrgList = async () => {
    setOrgLoading(true);
    setOrgError(null);
    const { data, error } = await supabase
      .from('org_structure')
      .select('*');
    if (error) setOrgError(error.message);
    setOrgList(data || []);
    setOrgLoading(false);
  };
  useEffect(() => {
    if (user && adminTab === 'org') loadOrgList();
  }, [user, adminTab]);

  // ดึงรายการ departments
  const loadDepartments = async () => {
    setDeptLoading(true);
    setDeptError(null);
    const { data, error } = await supabase
      .from('departments')
      .select('id, name, head, head_position, level')
      .order('level', { ascending: true });
    if (error) setDeptError(error.message);
    setDeptList(data || []);
    setDeptLoading(false);
  };
  useEffect(() => {
    if (user && adminTab === 'org') loadDepartments();
  }, [user, adminTab]);

  // ฟังก์ชันเพิ่ม/แก้ไขสื่อ/เอกสาร (สื่อประชาสัมพันธ์/เอกสาร)
  const handleMediaFormSubmit = async (e) => {
    e.preventDefault();
    setMediaFormLoading(true);
    setMediaUploadProgress({});
    
    let imageUrl = mediaForm.image_url;
    let fileUrl = mediaForm.file_url;
    const uploadedMediaUrls = [];

    // อัพโหลดรูปภาพหลายไฟล์
    if (mediaImageFiles.length > 0) {
      for (let i = 0; i < mediaImageFiles.length; i++) {
        const file = mediaImageFiles[i];
        const fileExt = file.name.split('.').pop();
        const fileName = `media-${Date.now()}-${i}.${fileExt}`;
        
        setMediaUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
        
        try {
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('media-images')
            .upload(fileName, file);
          
          if (uploadError) {
            console.error(`อัปโหลด ${file.name} ไม่สำเร็จ:`, uploadError);
            setMediaUploadProgress(prev => ({ ...prev, [file.name]: undefined }));
            continue;
          }
          
          const publicUrl = supabase.storage.from('media-images').getPublicUrl(fileName).data.publicUrl;
          uploadedMediaUrls.push({
            url: publicUrl,
            type: 'image',
            name: file.name
          });
          
          // ตั้งค่า image_url เป็นรูปแรกถ้ายังไม่มี
          if (!imageUrl) {
            imageUrl = publicUrl;
          }
          
          setMediaUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
        } catch (err) {
          console.error(`Error uploading ${file.name}:`, err);
          setMediaUploadProgress(prev => ({ ...prev, [file.name]: undefined }));
        }
      }
    }

    // อัพโหลดไฟล์เอกสารหลายไฟล์
    if (mediaFiles.length > 0) {
      for (let i = 0; i < mediaFiles.length; i++) {
        const file = mediaFiles[i];
        const fileExt = file.name.split('.').pop();
        const fileName = `media-file-${Date.now()}-${i}.${fileExt}`;
        
        setMediaUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
        
        try {
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('media-images')
            .upload(fileName, file);
          
          if (uploadError) {
            console.error(`อัปโหลด ${file.name} ไม่สำเร็จ:`, uploadError);
            setMediaUploadProgress(prev => ({ ...prev, [file.name]: undefined }));
            continue;
          }
          
          const publicUrl = supabase.storage.from('media-images').getPublicUrl(fileName).data.publicUrl;
          uploadedMediaUrls.push({
            url: publicUrl,
            type: file.type.startsWith('image/') ? 'image' : 'file',
            name: file.name
          });
          
          // ตั้งค่า file_url เป็นไฟล์แรกถ้ายังไม่มี
          if (!fileUrl) {
            fileUrl = publicUrl;
          }
          
          setMediaUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
        } catch (err) {
          console.error(`Error uploading ${file.name}:`, err);
          setMediaUploadProgress(prev => ({ ...prev, [file.name]: undefined }));
        }
      }
    }

    // รวม media_urls ที่มีอยู่แล้ว (ถ้ามี)
    let existingMediaUrls = [];
    if (mediaForm.media_urls) {
      try {
        existingMediaUrls = typeof mediaForm.media_urls === 'string' 
          ? JSON.parse(mediaForm.media_urls) 
          : mediaForm.media_urls;
      } catch (e) {
        existingMediaUrls = [];
      }
    }

    // รวม media URLs ทั้งหมด
    const allMediaUrls = [...existingMediaUrls, ...uploadedMediaUrls];
    const mediaUrlsJson = allMediaUrls.length > 0 ? JSON.stringify(allMediaUrls) : null;

    if (editingMedia) {
      // เตรียมข้อมูลสำหรับ update (ไม่ส่ง media_urls เพราะ column นี้ไม่มีในตาราง)
      const updateData = {
        title: mediaForm.title || '',
        date: mediaForm.date || null,
        description: mediaForm.description || null,
        image_url: imageUrl || mediaForm.image_url || null,
        file_url: fileUrl || mediaForm.file_url || null
        // ไม่ส่ง media_urls เพราะ column นี้ไม่มีในตาราง media_library
      };
      
      console.log('Updating media data:', updateData);
      
      const { error } = await supabase
        .from('media_library')
        .update(updateData)
        .eq('id', editingMedia.id);
      if (error) {
        console.error('Error updating media:', error);
        alert(`แก้ไขสื่อ/เอกสารไม่สำเร็จ: ${error.message || 'เกิดข้อผิดพลาด'}`);
        setMediaFormLoading(false);
        return;
      }
    } else {
      // เตรียมข้อมูลสำหรับ insert (ไม่ส่ง media_urls เพราะ column นี้ไม่มีในตาราง)
      const insertData = {
        title: mediaForm.title || '',
        date: mediaForm.date || null,
        description: mediaForm.description || null,
        image_url: imageUrl || null,
        file_url: fileUrl || null
        // ไม่ส่ง media_urls เพราะ column นี้ไม่มีในตาราง media_library
      };
      
      console.log('Inserting media data:', insertData);
      
      const { data, error } = await supabase
        .from('media_library')
        .insert([insertData])
        .select();
      
      if (error) {
        console.error('Error inserting media:', error);
        console.error('Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        alert(`เพิ่มสื่อ/เอกสารไม่สำเร็จ: ${error.message || 'เกิดข้อผิดพลาด'}`);
        setMediaFormLoading(false);
        return;
      }
      
      console.log('Media inserted successfully:', data);
    }
    
    setShowMediaForm(false);
    setEditingMedia(null);
    setMediaForm({ title: '', date: '', description: '', file_url: '', image_url: '', media_urls: '' });
    setMediaImageFiles([]);
    setMediaFiles([]);
    setMediaPreviewUrls([]);
    setMediaUploadProgress({});
    setMediaFormLoading(false);
    loadMediaList(); // Reload media list after submission
  };

  const handleEditMedia = (media) => {
    setEditingMedia(media);
    setMediaForm({
      title: media.title,
      date: media.date?.slice(0, 10),
      description: media.description || '',
      file_url: media.file_url,
      image_url: media.image_url,
      media_urls: media.media_urls || '',
    });
    setMediaImageFiles([]);
    setMediaFiles([]);
    setShowMediaForm(true);
  };
  const handleDeleteMedia = async (id) => {
    if (!window.confirm('ยืนยันลบสื่อ/เอกสารนี้?')) return;
    await supabase.from('media_library').delete().eq('id', id);
    loadMediaList();
  };

  const loadMediaList = async () => {
    setMediaLoading(true);
    setMediaError(null);
    const { data, error } = await supabase
      .from('media_library')
      .select('*');
    if (error) setMediaError(error.message);
    setMediaList(data || []);
    setMediaLoading(false);
  };
  useEffect(() => {
    if (user && adminTab === 'media') loadMediaList();
  }, [user, adminTab]);

  // สร้าง preview URLs สำหรับไฟล์ที่เลือก
  useEffect(() => {
    const urls = [];
    [...mediaImageFiles, ...mediaFiles].forEach(file => {
      if (file) {
        urls.push(URL.createObjectURL(file));
      }
    });
    setMediaPreviewUrls(urls);
    
    return () => {
      urls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [mediaImageFiles, mediaFiles]);

  // ฟังก์ชันเพิ่ม/แก้ไขหนังสือราชการ
  const handleDocFormSubmit = async (e) => {
    e.preventDefault();
    setDocFormLoading(true);
    let fileUrl = docForm.file_url;

    if (docFile) {
      const fileExt = docFile.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const { data: uploadData, error: uploadError } = await supabase.storage.from('doc-files').upload(fileName, docFile);
      if (uploadError) {
        setDocFormLoading(false);
        alert('อัปโหลดไฟล์ไม่สำเร็จ');
        return;
      }
      fileUrl = supabase.storage.from('doc-files').getPublicUrl(fileName).data.publicUrl;
    }

    if (editingDoc) {
      const { error } = await supabase
        .from('official_documents')
        .update({ ...docForm, file_url: fileUrl })
        .eq('id', editingDoc.id);
      if (error) alert('แก้ไขหนังสือราชการไม่สำเร็จ');
    } else {
      const { error } = await supabase
        .from('official_documents')
        .insert([{ ...docForm, file_url: fileUrl }]);
      if (error) alert('เพิ่มหนังสือราชการไม่สำเร็จ');
    }

    setShowDocForm(false);
    setEditingDoc(null);
    setDocForm({ title: '', document_number: '', date: '', category: '', file_url: '', description: '' });
    setDocFile(null);
    setDocFormLoading(false);
    loadDocList();
  };

  const loadDocList = async () => {
    setDocLoading(true);
    setDocError(null);
    const { data, error } = await supabase
      .from('official_documents')
      .select('*')
      .order('date', { ascending: false });
    if (error) setDocError(error.message);
    setDocList(data || []);
    setDocLoading(false);
  };

  const handleEditDoc = (doc) => {
    setEditingDoc(doc);
    setDocForm({
      title: doc.title,
      document_number: doc.document_number,
      date: doc.date,
      category: doc.category,
      file_url: doc.file_url,
      description: doc.description,
    });
    setShowDocForm(true);
  };

  const handleDeleteDoc = async (id) => {
    if (!window.confirm('ยืนยันลบหนังสือราชการนี้?')) return;
    await supabase.from('official_documents').delete().eq('id', id);
    loadDocList();
  };

  useEffect(() => {
    if (user && adminTab === 'documents') loadDocList();
  }, [user, adminTab]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={32} className="animate-spin mx-auto mb-4" />
          <p>กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-md mx-auto px-4 py-12">
          <div className="bg-card rounded-lg border border-border p-6">
            <div className="text-center mb-6">
              <Icon name="Shield" size={48} className="text-primary mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-foreground">เข้าสู่ระบบเจ้าหน้าที่</h1>
              <p className="text-muted-foreground mt-2">กรุณาเข้าสู่ระบบเพื่อจัดการเรื่องร้องทุกข์</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                label="อีเมล"
                type="email"
                placeholder="districtthoeng@gmail.com"
                value={loginForm.email}
                onChange={(e) => setLoginForm(prev => ({ ...prev, email: e.target.value }))}
                required
              />

              <Input
                label="รหัสผ่าน"
                type="password"
                placeholder="••••••••"
                value={loginForm.password}
                onChange={(e) => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
                required
              />

              {loginError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 text-sm">{loginError}</p>
                </div>
              )}

              <Button
                type="submit"
                loading={loginLoading}
                fullWidth
                className="mt-6"
              >
                {loginLoading ? 'กำลังเข้าสู่ระบบ...' : 'เข้าสู่ระบบ'}
              </Button>
            </form>

            <div className="mt-6 p-4 bg-accent/10 border border-accent/20 rounded-lg">
              <p className="text-sm text-muted-foreground text-center">
                <strong>หมายเหตุ:</strong> ระบบนี้สำหรับเจ้าหน้าที่เท่านั้น<br/>
                หากต้องการเข้าถึง กรุณาติดต่อผู้ดูแลระบบ
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
        <BreadcrumbTrail />
        
        {/* Admin Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">ศูนย์รับเรื่องร้องทุกข์ (Admin)</h1>
            <p className="text-muted-foreground">จัดการเรื่องร้องทุกข์และอัปเดตสถานะ</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className="text-sm text-muted-foreground">เข้าสู่ระบบในฐานะ</p>
              <p className="font-medium">{user?.email}</p>
            </div>
            <Button variant="outline" onClick={handleLogout}>
              <Icon name="LogOut" size={16} className="mr-2" />
              ออกจากระบบ
            </Button>
          </div>
        </div>

        {/* เพิ่มแท็บเมนูด้านบน */}
        <div className="flex gap-4 mb-6">
          <button onClick={() => setAdminTab('complaints')} className={`px-4 py-2 rounded ${adminTab === 'complaints' ? 'bg-primary text-white' : 'bg-muted text-foreground'}`}>เรื่องร้องทุกข์</button>
          <button onClick={() => setAdminTab('news')} className={`px-4 py-2 rounded ${adminTab === 'news' ? 'bg-primary text-white' : 'bg-muted text-foreground'}`}>ข่าวประชาสัมพันธ์</button>
          <button onClick={() => setAdminTab('org')} className={`px-4 py-2 rounded ${adminTab === 'org' ? 'bg-primary text-white' : 'bg-muted text-foreground'}`}>โครงสร้างองค์กร</button>
          <button onClick={() => setAdminTab('media')} className={`px-4 py-2 rounded ${adminTab === 'media' ? 'bg-primary text-white' : 'bg-muted text-foreground'}`}>สื่อ/เอกสาร</button>
          <button onClick={() => setAdminTab('documents')} className={`px-4 py-2 rounded ${adminTab === 'documents' ? 'bg-primary text-white' : 'bg-muted text-foreground'}`}>หนังสือราชการ</button>
          <button onClick={() => setAdminTab('procurement')} className={`px-4 py-2 rounded ${adminTab === 'procurement' ? 'bg-primary text-white' : 'bg-muted text-foreground'}`}>จัดซื้อจัดจ้าง</button>
          <button onClick={() => setAdminTab('appointments')} className={`px-4 py-2 rounded ${adminTab === 'appointments' ? 'bg-primary text-white' : 'bg-muted text-foreground'}`}>การจองคิว</button>
        </div>

        {/* เนื้อหาแต่ละแท็บ */}
        {adminTab === 'complaints' && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">ทั้งหมด</p>
                    <p className="text-2xl font-bold">{complaints.length}</p>
                  </div>
                  <Icon name="FileText" size={24} className="text-primary" />
                </div>
              </div>
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">รับเรื่องแล้ว</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {complaints.filter(c => c.status === 'received').length}
                    </p>
                  </div>
                  <Icon name="Clock" size={24} className="text-blue-600" />
                </div>
              </div>
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">อยู่ในระหว่างดำเนินการ</p>
                    <p className="text-2xl font-bold text-yellow-600">
                      {complaints.filter(c => c.status === 'in_progress').length}
                    </p>
                  </div>
                  <Icon name="Activity" size={24} className="text-yellow-600" />
                </div>
              </div>
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">ดำเนินการแล้วเสร็จ</p>
                    <p className="text-2xl font-bold text-green-600">
                      {complaints.filter(c => c.status === 'completed').length}
                    </p>
                  </div>
                  <Icon name="CheckCircle" size={24} className="text-green-600" />
                </div>
              </div>
            </div>

            {/* Complaints Table */}
            <div className="bg-card rounded-lg border border-border p-6">
              <h2 className="text-lg font-semibold mb-4">รายการเรื่องร้องทุกข์</h2>
              
              {complaints.length === 0 ? (
                <div className="text-center py-8">
                  <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">ยังไม่มีเรื่องร้องทุกข์</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left border-b border-border">
                        <th className="py-3 px-2">เลขที่คำร้อง</th>
                        <th className="py-3 px-2">ชื่อผู้ยื่น</th>
                        <th className="py-3 px-2">เบอร์โทร</th>
                        <th className="py-3 px-2">หมวดหมู่</th>
                        <th className="py-3 px-2">สถานะ</th>
                        <th className="py-3 px-2">วันที่ยื่น</th>
                        <th className="py-3 px-2">จัดการ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {complaints.map((complaint) => (
                        <tr key={complaint.id} className="border-b border-border hover:bg-muted/50">
                          <td className="py-3 px-2 font-mono text-xs">{complaint.ticket_no}</td>
                          <td className="py-3 px-2">{complaint.full_name}</td>
                          <td className="py-3 px-2">{complaint.contact_number}</td>
                          <td className="py-3 px-2">
                            <span className="px-2 py-1 bg-accent/20 text-accent-foreground rounded text-xs">
                              {complaint.category}
                            </span>
                          </td>
                          <td className="py-3 px-2">
                            <span className={`px-2 py-1 rounded text-xs ${
                              complaint.status === 'received' ? 'bg-blue-100 text-blue-700' :
                              complaint.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                              complaint.status === 'completed' ? 'bg-green-100 text-green-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {complaint.status === 'received' ? 'รับเรื่องแล้ว' :
                               complaint.status === 'in_progress' ? 'อยู่ในระหว่างดำเนินการ' :
                               complaint.status === 'completed' ? 'ดำเนินการแล้วเสร็จ' : complaint.status}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-muted-foreground">
                            {new Date(complaint.created_at).toLocaleDateString('th-TH')}
                          </td>
                          <td className="py-3 px-2">
                            <div className="flex space-x-2">
                              {complaint.status === 'received' && (
                                <Button 
                                  size="sm" 
                                  onClick={() => openStatusModal(complaint.id, 'in_progress')}
                                >
                                  <Icon name="Play" size={14} className="mr-1" />
                                  เริ่มดำเนินการ
                                </Button>
                              )}
                              {complaint.status === 'in_progress' && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => openStatusModal(complaint.id, 'completed')}
                                >
                                  <Icon name="CheckCircle" size={14} className="mr-1" />
                                  ดำเนินการแล้วเสร็จ
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleViewDetails(complaint)}
                              >
                                <Icon name="Eye" size={14} className="mr-1" />
                                ดูรายละเอียด
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
        {adminTab === 'news' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">จัดการข่าวประชาสัมพันธ์</h2>
              <button className="bg-primary text-white px-4 py-2 rounded" onClick={() => { setShowNewsForm(true); setEditingNews(null); setNewsForm({ title: '', content: '', publish_date: '', category: '', image_url: '', media_urls: [] }); setNewsMediaFiles([]); setUploadProgress({}); }}>เพิ่มข่าวใหม่</button>
            </div>
            {newsLoading ? <p>กำลังโหลด...</p> : newsError ? <p className="text-red-600">{newsError}</p> : (
              <table className="w-full text-sm mb-6">
                <thead>
                  <tr className="bg-muted text-foreground">
                    <th className="py-2 px-2">หัวข้อ</th>
                    <th className="py-2 px-2">หมวดหมู่</th>
                    <th className="py-2 px-2">วันที่</th>
                    <th className="py-2 px-2">รูป</th>
                    <th className="py-2 px-2">ไฟล์</th>
                    <th className="py-2 px-2">จัดการ</th>
                  </tr>
                </thead>
                <tbody>
                  {newsPaged.map(news => {
                    // นับจำนวนไฟล์จาก media_urls
                    let mediaCount = 0;
                    if (news.media_urls) {
                      try {
                        const mediaUrls = typeof news.media_urls === 'string' 
                          ? JSON.parse(news.media_urls) 
                          : (Array.isArray(news.media_urls) ? news.media_urls : []);
                        mediaCount = mediaUrls.length;
                      } catch (e) {
                        mediaCount = 0;
                      }
                    }
                    
                    return (
                      <tr key={news.id} className="border-b">
                        <td className="py-2 px-2">{news.title}</td>
                        <td className="py-2 px-2">{news.category}</td>
                        <td className="py-2 px-2">{news.publish_date ? new Date(news.publish_date).toLocaleDateString('th-TH') : ''}</td>
                        <td className="py-2 px-2">{news.image_url && <img src={news.image_url} alt="" className="w-16 h-10 object-cover rounded" />}</td>
                        <td className="py-2 px-2">
                          {mediaCount > 0 && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                              {mediaCount} ไฟล์
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-2">
                          <button className="text-blue-600 mr-2" onClick={() => handleEditNews(news)}>แก้ไข</button>
                          <button className="text-red-600" onClick={() => handleDeleteNews(news.id)}>ลบ</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {!newsLoading && !newsError && (
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3 text-sm">
                  <span>แสดงต่อหน้า:</span>
                  <select aria-label="แสดงต่อหน้า" className="min-w-[84px] border border-border rounded-md px-3 py-1.5 bg-white text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary" value={newsPageSize} onChange={(e)=> setNewsPageSize(Number(e.target.value))}>
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                  </select>
                </div>
                <div className="flex items-center gap-3">
                  <button className="px-3 py-1.5 border border-border bg-muted rounded-md hover:bg-muted/70 disabled:opacity-40 disabled:cursor-not-allowed" disabled={newsPage<=1} onClick={()=> setNewsPage(p=> Math.max(1, p-1))}>ก่อนหน้า</button>
                  <span className="text-sm">หน้า {newsPage} / {newsTotalPages}</span>
                  <button className="px-3 py-1.5 border border-border bg-muted rounded-md hover:bg-muted/70 disabled:opacity-40 disabled:cursor-not-allowed" disabled={newsPage>=newsTotalPages} onClick={()=> setNewsPage(p=> Math.min(newsTotalPages, p+1))}>ถัดไป</button>
                </div>
              </div>
            )}
            {/* ฟอร์มเพิ่ม/แก้ไขข่าว */}
            {showNewsForm && (
              <form onSubmit={handleNewsFormSubmit} className="bg-muted/30 p-6 rounded mb-6 max-w-xl">
                <h3 className="font-semibold mb-4">{editingNews ? 'แก้ไขข่าว' : 'เพิ่มข่าวใหม่'}</h3>
                <div className="mb-3">
                  <label className="block mb-1">หัวข้อ</label>
                  <input type="text" className="w-full border rounded px-3 py-2" value={newsForm.title} onChange={e => setNewsForm(f => ({ ...f, title: e.target.value }))} required />
                </div>
                <div className="mb-3">
                  <label className="block mb-1">เนื้อหา</label>
                  <textarea className="w-full border rounded px-3 py-2" rows={4} value={newsForm.content} onChange={e => setNewsForm(f => ({ ...f, content: e.target.value }))} required />
                </div>
                <div className="mb-3">
                  <label className="block mb-1">วันที่เผยแพร่</label>
                  <input type="date" className="w-full border rounded px-3 py-2" value={newsForm.publish_date} onChange={e => setNewsForm(f => ({ ...f, publish_date: e.target.value }))} required />
                </div>
                <div className="mb-3">
                  <label className="block mb-1">หมวดหมู่</label>
                  <select className="w-full border rounded px-3 py-2" value={newsForm.category} onChange={e => setNewsForm(f => ({ ...f, category: e.target.value }))} required>
                    <option value="">เลือกหมวดหมู่</option>
                    <option value="ประกาศ">ประกาศ</option>
                    <option value="ข่าวสาร">ข่าวสาร</option>
                    <option value="กิจกรรม">กิจกรรม</option>
                    <option value="บริการ">บริการ</option>
                    <option value="เตือนภัย">เตือนภัย</option>
                    <option value="ด่วน">ด่วน</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label className="block mb-1">
                    รูปภาพ/วีดีโอ <span className="text-xs text-muted-foreground">(สามารถเลือกหลายไฟล์ได้)</span>
                  </label>
                  <input 
                    type="file" 
                    accept="image/*,video/*" 
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      setNewsMediaFiles(files);
                    }}
                    className="w-full border rounded px-3 py-2"
                  />
                  
                  {/* แสดงไฟล์ที่เลือกใหม่ */}
                  {newsMediaFiles.length > 0 && (
                    <div className="mt-3 space-y-3">
                      <p className="text-sm font-medium text-foreground">ไฟล์ที่เลือก ({newsMediaFiles.length} ไฟล์):</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {newsMediaFiles.map((file, index) => {
                          const isVideo = file.type.startsWith('video/');
                          const fileSize = (file.size / 1024 / 1024).toFixed(2); // MB
                          const previewUrl = previewUrls[index] || '';
                          return (
                            <div key={index} className="relative border border-border rounded overflow-hidden bg-card">
                              {isVideo ? (
                                <div>
                                  <video 
                                    src={previewUrl} 
                                    className="w-full h-24 object-cover"
                                    controls={false}
                                  />
                                  <div className="p-2">
                                    <p className="text-xs text-foreground truncate">{file.name}</p>
                                    <p className="text-xs text-muted-foreground">{fileSize} MB</p>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <img 
                                    src={previewUrl} 
                                    alt={file.name}
                                    className="w-full h-24 object-cover"
                                  />
                                  <div className="p-2">
                                    <p className="text-xs text-foreground truncate">{file.name}</p>
                                    <p className="text-xs text-muted-foreground">{fileSize} MB</p>
                                  </div>
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  const newFiles = newsMediaFiles.filter((_, i) => i !== index);
                                  setNewsMediaFiles(newFiles);
                                }}
                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                title="ลบไฟล์"
                              >
                                <Icon name="X" size={12} />
                              </button>
                              <span className={`absolute top-1 left-1 text-xs px-2 py-0.5 rounded ${isVideo ? 'bg-purple-500 text-white' : 'bg-blue-500 text-white'}`}>
                                {isVideo ? 'วีดีโอ' : 'รูปภาพ'}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* แสดงสถานะการอัปโหลด */}
                      {Object.keys(uploadProgress).length > 0 && (
                        <div className="mt-2 space-y-1">
                          {Object.entries(uploadProgress).map(([index, status]) => (
                            <p key={index} className="text-xs text-muted-foreground">
                              {status}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* แสดงไฟล์ที่อัปโหลดไว้แล้ว */}
                  {newsForm.media_urls && newsForm.media_urls.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <p className="text-sm font-medium text-foreground">ไฟล์ที่อัปโหลดไว้แล้ว ({newsForm.media_urls.length} ไฟล์):</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {newsForm.media_urls.map((media, index) => (
                          <div key={index} className="relative group border border-border rounded overflow-hidden bg-card">
                            {media.type === 'video' ? (
                              <div>
                                <video 
                                  src={media.url} 
                                  className="w-full h-32 object-cover"
                                  controls
                                />
                                <div className="p-2">
                                  <p className="text-xs text-foreground truncate">{media.name || 'วีดีโอ'}</p>
                                </div>
                                <span className="absolute top-1 left-1 text-xs px-2 py-0.5 rounded bg-purple-500 text-white">
                                  วีดีโอ
                                </span>
                              </div>
                            ) : (
                              <div>
                                <img 
                                  src={media.url} 
                                  alt={media.name || 'รูปภาพ'} 
                                  className="w-full h-24 object-cover"
                                />
                                <div className="p-2">
                                  <p className="text-xs text-foreground truncate">{media.name || 'รูปภาพ'}</p>
                                </div>
                                <span className="absolute top-1 left-1 text-xs px-2 py-0.5 rounded bg-blue-500 text-white">
                                  รูปภาพ
                                </span>
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                const newMediaUrls = newsForm.media_urls.filter((_, i) => i !== index);
                                setNewsForm({ ...newsForm, media_urls: newMediaUrls });
                                // ถ้าลบรูปภาพที่ใช้เป็น image_url ให้ใช้รูปภาพถัดไป
                                if (media.url === newsForm.image_url && media.type === 'image') {
                                  const firstImage = newMediaUrls.find(m => m.type === 'image');
                                  if (firstImage) {
                                    setNewsForm({ ...newsForm, media_urls: newMediaUrls, image_url: firstImage.url });
                                  } else {
                                    setNewsForm({ ...newsForm, media_urls: newMediaUrls, image_url: '' });
                                  }
                                } else {
                                  setNewsForm({ ...newsForm, media_urls: newMediaUrls });
                                }
                              }}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                              title="ลบไฟล์"
                            >
                              <Icon name="X" size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* แสดงรูปภาพหลัก (สำหรับแสดงใน preview) */}
                  {newsForm.image_url && !newsMediaFiles.length && (
                    <div className="mt-3">
                      <p className="text-sm font-medium text-foreground mb-2">รูปภาพหลัก:</p>
                      <img src={newsForm.image_url} alt="Preview" className="w-32 h-20 object-cover rounded border border-border" />
                    </div>
                  )}
                </div>
                <div className="flex gap-2 mt-4">
                  <button type="submit" className="bg-primary text-white px-4 py-2 rounded" disabled={newsFormLoading}>
                    {newsFormLoading ? 'กำลังบันทึก...' : 'บันทึก'}
                  </button>
                  <button 
                    type="button" 
                    className="bg-muted text-foreground px-4 py-2 rounded" 
                    onClick={() => { 
                      setShowNewsForm(false); 
                      setEditingNews(null); 
                      setNewsForm({ title: '', content: '', publish_date: '', category: '', image_url: '', media_urls: [] }); 
                      setNewsMediaFiles([]);
                      setUploadProgress({});
                    }}
                  >
                    ยกเลิก
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
        {adminTab === 'org' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">จัดการโครงสร้างองค์กร</h2>
              <button className="bg-primary text-white px-4 py-2 rounded" onClick={() => { setShowOrgForm(true); setEditingOrg(null); setOrgForm({ name: '', position: '', phone: '', image_url: '' }); setOrgImageFile(null); }}>เพิ่มบุคลากร</button>
            </div>
            {/* แก้ชื่อหน่วยงาน/หัวหน้า/ตำแหน่งหัวหน้า (departments) */}
            <div className="bg-card border border-border rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium">หน่วยงาน (ชื่อ-หัวหน้า-ตำแหน่ง)</h3>
                <button className="text-sm bg-muted px-3 py-1 rounded" onClick={loadDepartments}>รีเฟรช</button>
              </div>
              {deptLoading ? (
                <p>กำลังโหลด...</p>
              ) : deptError ? (
                <p className="text-red-600">{deptError}</p>
              ) : (
                <table className="w-full text-sm mb-4">
                  <thead>
                    <tr className="bg-muted text-foreground">
                      <th className="py-2 px-2">ระดับ</th>
                      <th className="py-2 px-2">หน่วยงาน</th>
                      <th className="py-2 px-2">หัวหน้า</th>
                      <th className="py-2 px-2">ตำแหน่งหัวหน้า</th>
                      <th className="py-2 px-2">จัดการ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deptList.map((d) => (
                      <tr key={d.id} className="border-b">
                        <td className="py-2 px-2">{d.level}</td>
                        <td className="py-2 px-2">{d.name}</td>
                        <td className="py-2 px-2">{d.head}</td>
                        <td className="py-2 px-2">{d.head_position}</td>
                        <td className="py-2 px-2">
                          <button className="text-blue-600 mr-2" onClick={() => { setEditingDept(d); setDeptForm({ name: d.name || '', head: d.head || '', head_position: d.head_position || '' }); setShowDeptForm(true); }}>แก้ไข</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {showDeptForm && (
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  setDeptFormLoading(true);
                  const payload = { name: deptForm.name, head: deptForm.head, head_position: deptForm.head_position };
                  let resp;
                  if (editingDept) {
                    resp = await supabase.from('departments').update(payload).eq('id', editingDept.id);
                  } else {
                    resp = await supabase.from('departments').insert([payload]);
                  }
                  const { error } = resp;
                  if (error) alert('บันทึกไม่สำเร็จ: ' + error.message);
                  setShowDeptForm(false);
                  setEditingDept(null);
                  setDeptForm({ name: '', head: '', head_position: '' });
                  setDeptFormLoading(false);
                  loadDepartments();
                }} className="bg-muted/30 p-4 rounded">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block mb-1 text-sm">หน่วยงาน</label>
                      <input type="text" className="w-full border rounded px-3 py-2" value={deptForm.name} onChange={(e) => setDeptForm(f => ({ ...f, name: e.target.value }))} required />
                    </div>
                    <div>
                      <label className="block mb-1 text-sm">หัวหน้า</label>
                      <input type="text" className="w-full border rounded px-3 py-2" value={deptForm.head} onChange={(e) => setDeptForm(f => ({ ...f, head: e.target.value }))} />
                    </div>
                    <div>
                      <label className="block mb-1 text-sm">ตำแหน่งหัวหน้า</label>
                      <input type="text" className="w-full border rounded px-3 py-2" value={deptForm.head_position} onChange={(e) => setDeptForm(f => ({ ...f, head_position: e.target.value }))} />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button type="submit" className="bg-primary text-white px-4 py-2 rounded" disabled={deptFormLoading}>{deptFormLoading ? 'กำลังบันทึก...' : 'บันทึก'}</button>
                    <button type="button" className="bg-muted text-foreground px-4 py-2 rounded" onClick={() => { setShowDeptForm(false); setEditingDept(null); setDeptForm({ name: '', head: '', head_position: '' }); }}>ยกเลิก</button>
                  </div>
                </form>
              )}
            </div>
            {orgLoading ? <p>กำลังโหลด...</p> : orgError ? <p className="text-red-600">{orgError}</p> : (
              <table className="w-full text-sm mb-4">
                <thead>
                  <tr className="bg-muted text-foreground">
                    <th className="py-2 px-2">ชื่อ</th>
                    <th className="py-2 px-2">ตำแหน่ง</th>
                    <th className="py-2 px-2">เบอร์โทร</th>
                    <th className="py-2 px-2">รูป</th>
                    <th className="py-2 px-2">จัดการ</th>
                  </tr>
                </thead>
                <tbody>
                  {orgPaged.map((person, idx) => (
                    <tr key={idx} className="border-b">
                      <td className="py-2 px-2">{person.name}</td>
                      <td className="py-2 px-2">{person.position}</td>
                      <td className="py-2 px-2">{person.phone}</td>
                      <td className="py-2 px-2">{person.image_url && <img src={person.image_url} alt="" className="w-16 h-16 object-cover rounded-full" />}</td>
                      <td className="py-2 px-2">
                        <button className="text-blue-600 mr-2" onClick={() => handleEditOrg(person)}>แก้ไข</button>
                        <button className="text-green-600 mr-2" onClick={() => handleEditOrg(person)}>แก้ไขรูป</button>
                        <button className="text-amber-600 mr-2" onClick={() => handleResetOrgImage(person)}>ใช้รูปจากโฟลเดอร์</button>
                        <button className="text-red-600" onClick={() => handleDeleteOrg(person.id)}>ลบ</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {!orgLoading && !orgError && (
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3 text-sm">
                  <span>แสดงต่อหน้า:</span>
                  <select aria-label="แสดงต่อหน้า" className="min-w-[84px] border border-border rounded-md px-3 py-1.5 bg-white text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary" value={orgPageSize} onChange={(e)=> setOrgPageSize(Number(e.target.value))}>
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                  </select>
                </div>
                <div className="flex items-center gap-3">
                  <button className="px-3 py-1.5 border border-border bg-muted rounded-md hover:bg-muted/70 disabled:opacity-40 disabled:cursor-not-allowed" disabled={orgPage<=1} onClick={()=> setOrgPage(p=> Math.max(1, p-1))}>ก่อนหน้า</button>
                  <span className="text-sm">หน้า {orgPage} / {orgTotalPages}</span>
                  <button className="px-3 py-1.5 border border-border bg-muted rounded-md hover:bg-muted/70 disabled:opacity-40 disabled:cursor-not-allowed" disabled={orgPage>=orgTotalPages} onClick={()=> setOrgPage(p=> Math.min(orgTotalPages, p+1))}>ถัดไป</button>
                </div>
              </div>
            )}
            {/* ฟอร์มเพิ่ม/แก้ไขบุคลากร (mockup) */}
            {showOrgForm && (
              <form ref={orgFormRef} onSubmit={handleOrgFormSubmit} className="bg-muted/30 p-6 rounded mb-6 max-w-xl">
                <h3 className="font-semibold mb-4">{editingOrg ? 'แก้ไขบุคลากร' : 'เพิ่มบุคลากร'}</h3>
                <div className="mb-3">
                  <label className="block mb-1">ชื่อ</label>
                  <input type="text" className="w-full border rounded px-3 py-2" value={orgForm.name} onChange={e => setOrgForm(f => ({ ...f, name: e.target.value }))} required />
                </div>
                <div className="mb-3">
                  <label className="block mb-1">ตำแหน่ง</label>
                  <input type="text" className="w-full border rounded px-3 py-2" value={orgForm.position} onChange={e => setOrgForm(f => ({ ...f, position: e.target.value }))} required />
                </div>
                <div className="mb-3">
                  <label className="block mb-1">เบอร์โทร</label>
                  <input type="text" className="w-full border rounded px-3 py-2" value={orgForm.phone} onChange={e => setOrgForm(f => ({ ...f, phone: e.target.value }))} />
                </div>
                <div className="mb-3">
                  <label className="block mb-1">รูปภาพ</label>
                  <input type="file" accept="image/*" onChange={e => setOrgImageFile(e.target.files[0])} />
                  {orgForm.image_url && <img src={orgForm.image_url} alt="" className="w-32 h-32 object-cover rounded-full mt-2" />}
                </div>
                <div className="flex gap-2 mt-4">
                  <button type="submit" className="bg-primary text-white px-4 py-2 rounded" disabled={orgFormLoading}>{orgFormLoading ? 'กำลังบันทึก...' : 'บันทึก'}</button>
                  <button type="button" className="bg-muted text-foreground px-4 py-2 rounded" onClick={() => { setShowOrgForm(false); setEditingOrg(null); setOrgForm({ name: '', position: '', phone: '', image_url: '' }); setOrgImageFile(null); }}>ยกเลิก</button>
                </div>
              </form>
            )}
          </div>
        )}
        {adminTab === 'media' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">จัดการสื่อประชาสัมพันธ์/เอกสาร</h2>
              <button className="bg-primary text-white px-4 py-2 rounded" onClick={() => { setShowMediaForm(true); setEditingMedia(null); setMediaForm({ title: '', date: '', description: '', file_url: '', image_url: '', media_urls: '' }); setMediaImageFiles([]); setMediaFiles([]); setMediaPreviewUrls([]); }}>เพิ่มสื่อ/เอกสาร</button>
            </div>
            {mediaLoading ? <p>กำลังโหลด...</p> : mediaError ? <p className="text-red-600">{mediaError}</p> : (
              <table className="w-full text-sm mb-6">
                <thead>
                  <tr className="bg-muted text-foreground">
                    <th className="py-2 px-2">หัวข้อ</th>
                    <th className="py-2 px-2">วันที่</th>
                    <th className="py-2 px-2">รูป</th>
                    <th className="py-2 px-2">ไฟล์</th>
                    <th className="py-2 px-2">จัดการ</th>
                  </tr>
                </thead>
                <tbody>
                  {mediaList.map((media, idx) => (
                    <tr key={idx} className="border-b">
                      <td className="py-2 px-2">{media.title}</td>
                      <td className="py-2 px-2">{media.date}</td>
                      <td className="py-2 px-2">{media.image_url && <img src={media.image_url} alt="" className="w-16 h-10 object-cover rounded" />}</td>
                      <td className="py-2 px-2">{media.file_url && <a href={media.file_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">ดาวน์โหลด</a>}</td>
                      <td className="py-2 px-2">
                        <button className="text-blue-600 mr-2" onClick={() => { setEditingMedia(media); setMediaForm(media); setShowMediaForm(true); }}>แก้ไข</button>
                        <button className="text-red-600" onClick={() => handleDeleteMedia(media.id)}>ลบ</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {/* ฟอร์มเพิ่ม/แก้ไขสื่อ/เอกสาร (mockup) */}
            {showMediaForm && (
              <form onSubmit={handleMediaFormSubmit} className="bg-muted/30 p-6 rounded mb-6 max-w-xl">
                <h3 className="font-semibold mb-4">{editingMedia ? 'แก้ไขสื่อ/เอกสาร' : 'เพิ่มสื่อ/เอกสาร'}</h3>
                <div className="mb-3">
                  <label className="block mb-1">หัวข้อ</label>
                  <input type="text" className="w-full border rounded px-3 py-2" value={mediaForm.title} onChange={e => setMediaForm(f => ({ ...f, title: e.target.value }))} required />
                </div>
                <div className="mb-3">
                  <label className="block mb-1">วันที่เผยแพร่ <span className="text-gray-500 text-xs">(วันที่ของสื่อ/เอกสาร)</span></label>
                  <input 
                    type="date" 
                    className="w-full border rounded px-3 py-2" 
                    value={mediaForm.date} 
                    onChange={e => setMediaForm(f => ({ ...f, date: e.target.value }))} 
                    required 
                    placeholder="เลือกวันที่เผยแพร่สื่อ/เอกสาร"
                  />
                </div>
                <div className="mb-3">
                  <label className="block mb-1">รายละเอียด</label>
                  <textarea 
                    className="w-full border rounded px-3 py-2" 
                    rows="4"
                    value={mediaForm.description || ''} 
                    onChange={e => setMediaForm(f => ({ ...f, description: e.target.value }))} 
                    placeholder="กรอกรายละเอียดสื่อ/เอกสาร"
                  />
                </div>
                <div className="mb-3">
                  <label className="block mb-1">รูปภาพ (สามารถเลือกหลายไฟล์ได้)</label>
                  <input 
                    type="file" 
                    accept="image/*" 
                    multiple
                    onChange={e => setMediaImageFiles(Array.from(e.target.files || []))} 
                  />
                  
                  {/* แสดง preview รูปภาพที่เลือก */}
                  {mediaImageFiles.length > 0 && (
                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {mediaImageFiles.map((file, index) => (
                        <div key={index} className="relative">
                          <img 
                            src={URL.createObjectURL(file)} 
                            alt={file.name}
                            className="w-full h-24 object-cover rounded border"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newFiles = [...mediaImageFiles];
                              newFiles.splice(index, 1);
                              setMediaImageFiles(newFiles);
                            }}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                          >
                            ×
                          </button>
                          <p className="text-xs text-gray-600 mt-1 truncate">{file.name}</p>
                          {mediaUploadProgress[file.name] !== undefined && (
                            <div className="mt-1">
                              <div className="w-full bg-gray-200 rounded-full h-1">
                                <div 
                                  className="bg-blue-600 h-1 rounded-full" 
                                  style={{ width: `${mediaUploadProgress[file.name]}%` }}
                                ></div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* แสดงรูปภาพที่มีอยู่แล้ว */}
                  {mediaForm.image_url && !mediaImageFiles.length && (
                    <img src={mediaForm.image_url} alt="" className="w-32 h-20 object-cover rounded mt-2" />
                  )}
                  
                  {/* แสดง media_urls ที่มีอยู่แล้ว */}
                  {mediaForm.media_urls && (() => {
                    try {
                      const existingUrls = typeof mediaForm.media_urls === 'string' 
                        ? JSON.parse(mediaForm.media_urls) 
                        : mediaForm.media_urls;
                      if (Array.isArray(existingUrls) && existingUrls.length > 0) {
                        return (
                          <div className="mt-3">
                            <p className="text-sm font-medium mb-2">ไฟล์ที่อัพโหลดแล้ว:</p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                              {existingUrls.map((item, index) => (
                                <div key={index} className="relative">
                                  {item.type === 'image' ? (
                                    <img 
                                      src={item.url} 
                                      alt={item.name}
                                      className="w-full h-24 object-cover rounded border"
                                    />
                                  ) : (
                                    <div className="w-full h-24 bg-gray-100 rounded border flex items-center justify-center">
                                      <span className="text-xs">📄 {item.name}</span>
                                    </div>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newUrls = existingUrls.filter((_, i) => i !== index);
                                      setMediaForm(f => ({ ...f, media_urls: JSON.stringify(newUrls) }));
                                    }}
                                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }
                    } catch (e) {
                      return null;
                    }
                    return null;
                  })()}
                </div>
                <div className="mb-3">
                  <label className="block mb-1">ไฟล์เอกสาร (สามารถเลือกหลายไฟล์ได้)</label>
                  <input 
                    type="file" 
                    multiple
                    onChange={e => setMediaFiles(Array.from(e.target.files || []))} 
                  />
                  
                  {/* แสดง preview ไฟล์ที่เลือก */}
                  {mediaFiles.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {mediaFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                          <div className="flex items-center space-x-2">
                            <span className="text-lg">📄</span>
                            <div>
                              <p className="text-sm font-medium">{file.name}</p>
                              <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const newFiles = [...mediaFiles];
                              newFiles.splice(index, 1);
                              setMediaFiles(newFiles);
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            ×
                          </button>
                          {mediaUploadProgress[file.name] !== undefined && (
                            <div className="ml-2 w-24">
                              <div className="w-full bg-gray-200 rounded-full h-1">
                                <div 
                                  className="bg-blue-600 h-1 rounded-full" 
                                  style={{ width: `${mediaUploadProgress[file.name]}%` }}
                                ></div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* แสดงไฟล์ที่มีอยู่แล้ว */}
                  {mediaForm.file_url && !mediaFiles.length && (
                    <a href={mediaForm.file_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline block mt-2">ดูไฟล์</a>
                  )}
                </div>
                <div className="flex gap-2 mt-4">
                  <button type="submit" className="bg-primary text-white px-4 py-2 rounded" disabled={mediaFormLoading}>{mediaFormLoading ? 'กำลังบันทึก...' : 'บันทึก'}</button>
                  <button type="button" className="bg-muted text-foreground px-4 py-2 rounded" onClick={() => { setShowMediaForm(false); setEditingMedia(null); setMediaForm({ title: '', date: '', description: '', file_url: '', image_url: '', media_urls: '' }); setMediaImageFiles([]); setMediaFiles([]); setMediaPreviewUrls([]); }}>ยกเลิก</button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>

      {/* Complaint Detail Modal */}
      {showDetailModal && selectedComplaint && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg border border-border max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-foreground">รายละเอียดเรื่องร้องทุกข์</h2>
                  <p className="text-muted-foreground">เลขที่คำร้อง: {selectedComplaint.ticket_no}</p>
                </div>
                <Button variant="outline" onClick={closeDetailModal}>
                  <Icon name="X" size={16} />
                </Button>
              </div>

              {/* Complaint Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Left Column - Basic Info */}
                <div className="space-y-4">
                  <div className="bg-muted/30 rounded-lg p-4">
                    <h3 className="font-semibold mb-3">ข้อมูลผู้ยื่นเรื่อง</h3>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">ชื่อ-นามสกุล:</span>
                        <span className="ml-2 font-medium">{selectedComplaint.full_name}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">เบอร์โทรศัพท์:</span>
                        <span className="ml-2 font-medium">{selectedComplaint.contact_number}</span>
                      </div>
                      {selectedComplaint.email && (
                        <div>
                          <span className="text-muted-foreground">อีเมล:</span>
                          <span className="ml-2 font-medium">{selectedComplaint.email}</span>
                        </div>
                      )}
                      <div>
                        <span className="text-muted-foreground">ไอดีไลน์:</span>
                        <span className="ml-2 font-medium">{selectedComplaint.line_id}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-muted/30 rounded-lg p-4">
                    <h3 className="font-semibold mb-3">ข้อมูลเรื่องร้องทุกข์</h3>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">หมวดหมู่:</span>
                        <span className="ml-2 px-2 py-1 bg-accent/20 text-accent-foreground rounded text-xs">
                          {selectedComplaint.category}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">สถานะ:</span>
                        <span className={`ml-2 px-2 py-1 rounded text-xs ${
                          selectedComplaint.status === 'received' ? 'bg-blue-100 text-blue-700' :
                          selectedComplaint.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                          selectedComplaint.status === 'completed' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {
                            selectedComplaint.status === 'received' ? 'รับแล้ว' :
                            selectedComplaint.status === 'in_progress' ? 'อยู่ในระหว่างดำเนินการ' :
                            selectedComplaint.status === 'completed' ? 'ดำเนินการแล้วเสร็จ' :
                            'ไม่ทราบสถานะ'
                          }
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">วันที่ยื่นเรื่อง:</span>
                        <span className="ml-2 font-medium">
                          {new Date(selectedComplaint.created_at).toLocaleDateString('th-TH', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Complaint Details */}
                <div className="space-y-4">
                  <div className="bg-muted/30 rounded-lg p-4">
                    <h3 className="font-semibold mb-3">รายละเอียดเรื่องร้องทุกข์</h3>
                    <div className="space-y-3">
                      <div>
                        <span className="text-muted-foreground text-sm">หัวข้อ:</span>
                        <p className="mt-1 font-medium">{selectedComplaint.subject}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground text-sm">รายละเอียด:</span>
                        <p className="mt-1 text-sm leading-relaxed whitespace-pre-wrap">
                          {selectedComplaint.description}
                        </p>
                      </div>
                      {selectedComplaint.location && (
                        <div>
                          <span className="text-muted-foreground text-sm">สถานที่:</span>
                          <p className="mt-1 text-sm">{selectedComplaint.location}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Attachments Section */}
                  {(() => {
                    // Handle both array and JSON string formats
                    let attachments = selectedComplaint.attachments;
                    if (typeof attachments === 'string') {
                      try {
                        attachments = JSON.parse(attachments);
                      } catch (e) {
                        attachments = null;
                      }
                    }
                    return attachments && Array.isArray(attachments) && attachments.length > 0 ? (
                      <div className="bg-muted/30 rounded-lg p-4">
                        <h3 className="font-semibold mb-3">ไฟล์แนบ</h3>
                        <div className="space-y-2">
                          {attachments.map((attachment, index) => {
                          const isImage = attachment.name?.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i);
                          const isPdf = attachment.name?.match(/\.(pdf)$/i);
                          const fileExtension = attachment.name?.split('.').pop()?.toLowerCase() || '';
                          
                          return (
                            <div 
                              key={index} 
                              className="flex items-center justify-between p-3 bg-background rounded-lg border border-border hover:bg-muted/50 transition-colors"
                            >
                              <div className="flex items-center space-x-3 flex-1 min-w-0">
                                <div className="flex-shrink-0">
                                  {isImage ? (
                                    <Icon name="Image" size={20} className="text-primary" />
                                  ) : isPdf ? (
                                    <Icon name="FileText" size={20} className="text-red-600" />
                                  ) : (
                                    <Icon name="File" size={20} className="text-muted-foreground" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-foreground truncate" title={attachment.name}>
                                    {attachment.name || `ไฟล์แนบ ${index + 1}`}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {fileExtension.toUpperCase()} • {isImage ? 'รูปภาพ' : isPdf ? 'เอกสาร PDF' : 'เอกสาร'}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2 flex-shrink-0">
                                {(isImage || isPdf) && attachment.url && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      if (isPdf) {
                                        // Validate URL before setting
                                        const url = typeof attachment.url === 'string' && attachment.url.startsWith('http') 
                                          ? attachment.url 
                                          : null;
                                        if (url) {
                                          setPdfPreviewUrl(url);
                                          setShowPdfPreview(true);
                                        } else {
                                          alert('ไม่สามารถแสดงพรีวิว PDF ได้: URL ไม่ถูกต้อง');
                                        }
                                      } else {
                                        window.open(attachment.url, '_blank');
                                      }
                                    }}
                                    title={isPdf ? "ดูพรีวิว PDF" : "ดูรูปภาพ"}
                                  >
                                    <Icon name="Eye" size={14} className="mr-1" />
                                    ดู
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    if (attachment.url) {
                                      const link = document.createElement('a');
                                      link.href = attachment.url;
                                      link.download = attachment.name || `attachment-${index + 1}`;
                                      link.target = '_blank';
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }
                                  }}
                                  title="ดาวน์โหลดไฟล์"
                                >
                                  <Icon name="Download" size={14} className="mr-1" />
                                  ดาวน์โหลด
                                </Button>
                              </div>
                            </div>
                          );
                          })}
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-border">
                {selectedComplaint.status === 'received' && (
                  <Button 
                    onClick={() => {
                      openStatusModal(selectedComplaint.id, 'in_progress');
                      closeDetailModal();
                    }}
                  >
                    <Icon name="Play" size={16} className="mr-2" />
                    เริ่มดำเนินการ
                  </Button>
                )}
                {selectedComplaint.status === 'in_progress' && (
                  <Button 
                    onClick={() => {
                      openStatusModal(selectedComplaint.id, 'completed');
                      closeDetailModal();
                    }}
                  >
                    <Icon name="CheckCircle" size={16} className="mr-2" />
                    ดำเนินการแล้วเสร็จ
                  </Button>
                )}
                <Button variant="outline" onClick={closeDetailModal}>
                  ปิด
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PDF Preview Modal */}
      {showPdfPreview && pdfPreviewUrl && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-card rounded-lg border border-border max-w-6xl w-full max-h-[95vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-xl font-bold text-foreground">พรีวิวเอกสาร PDF</h2>
              <Button variant="outline" onClick={() => {
                setShowPdfPreview(false);
                setPdfPreviewUrl(null);
              }}>
                <Icon name="X" size={16} />
              </Button>
            </div>
            <div className="flex-1 overflow-hidden p-4">
              {(() => {
                // Validate URL - check if it's a valid URL string
                const isValidUrl = typeof pdfPreviewUrl === 'string' && 
                                  pdfPreviewUrl.startsWith('http') && 
                                  !pdfPreviewUrl.startsWith('{');
                
                if (!isValidUrl) {
                  return (
                    <div className="flex flex-col items-center justify-center h-full p-8">
                      <Icon name="AlertCircle" size={48} className="text-warning mb-4" />
                      <p className="text-lg font-medium text-foreground mb-2">
                        ไม่สามารถแสดงพรีวิว PDF ได้
                      </p>
                      <p className="text-sm text-muted-foreground text-center mb-4">
                        URL ของไฟล์ไม่ถูกต้องหรือไฟล์ไม่พร้อมใช้งาน
                      </p>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowPdfPreview(false);
                          setPdfPreviewUrl(null);
                        }}
                      >
                        ปิด
                      </Button>
                    </div>
                  );
                }
                
                return (
                  <>
                    <iframe
                      src={`${pdfPreviewUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                      className="w-full h-full border border-border rounded"
                      title="PDF Preview"
                      style={{ minHeight: '600px' }}
                      type="application/pdf"
                    />
                    <div className="mt-2 text-sm text-muted-foreground text-center">
                      หากไม่สามารถแสดง PDF ได้ กรุณาใช้ปุ่มดาวน์โหลดเพื่อเปิดไฟล์
                    </div>
                  </>
                );
              })()}
            </div>
            <div className="flex justify-end space-x-3 p-4 border-t border-border">
              <Button
                variant="outline"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = pdfPreviewUrl;
                  link.download = pdfPreviewUrl.split('/').pop() || 'document.pdf';
                  link.target = '_blank';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                <Icon name="Download" size={16} className="mr-2" />
                ดาวน์โหลด
              </Button>
              <Button variant="outline" onClick={() => {
                setShowPdfPreview(false);
                setPdfPreviewUrl(null);
              }}>
                ปิด
              </Button>
            </div>
          </div>
        </div>
      )}

      {adminTab === 'documents' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">จัดการหนังสือราชการ</h2>
            <button className="bg-primary text-white px-4 py-2 rounded" onClick={() => { setShowDocForm(true); setEditingDoc(null); setDocForm({ title: '', document_number: '', date: '', category: '', file_url: '', description: '' }); setDocFile(null); }}>เพิ่มหนังสือราชการ</button>
          </div>
          {docLoading ? <p>กำลังโหลด...</p> : docError ? <p className="text-red-600">{docError}</p> : (
            <table className="w-full text-sm mb-4">
              <thead>
                <tr className="bg-muted text-foreground">
                  <th className="py-2 px-2">เลขที่หนังสือ</th>
                  <th className="py-2 px-2">หัวข้อ</th>
                  <th className="py-2 px-2">หมวดหมู่</th>
                  <th className="py-2 px-2">วันที่</th>
                  <th className="py-2 px-2">ไฟล์</th>
                  <th className="py-2 px-2">จัดการ</th>
                </tr>
              </thead>
              <tbody>
                {docPaged.map((doc) => (
                  <tr key={doc.id} className="border-b">
                    <td className="py-2 px-2">{doc.document_number}</td>
                    <td className="py-2 px-2">{doc.title}</td>
                    <td className="py-2 px-2">{doc.category}</td>
                    <td className="py-2 px-2">{doc.date}</td>
                    <td className="py-2 px-2">{doc.file_url && <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">ดูไฟล์</a>}</td>
                    <td className="py-2 px-2">
                      <button className="text-blue-600 mr-2" onClick={() => handleEditDoc(doc)}>แก้ไข</button>
                      <button className="text-red-600" onClick={() => handleDeleteDoc(doc.id)}>ลบ</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!docLoading && !docError && (
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3 text-sm">
                <span>แสดงต่อหน้า:</span>
                <select aria-label="แสดงต่อหน้า" className="min-w-[84px] border border-border rounded-md px-3 py-1.5 bg-white text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary" value={docPageSize} onChange={(e)=> setDocPageSize(Number(e.target.value))}>
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                </select>
              </div>
              <div className="flex items-center gap-3">
                <button className="px-3 py-1.5 border border-border bg-muted rounded-md hover:bg-muted/70 disabled:opacity-40 disabled:cursor-not-allowed" disabled={docPage<=1} onClick={()=> setDocPage(p=> Math.max(1, p-1))}>ก่อนหน้า</button>
                <span className="text-sm">หน้า {docPage} / {docTotalPages}</span>
                <button className="px-3 py-1.5 border border-border bg-muted rounded-md hover:bg-muted/70 disabled:opacity-40 disabled:cursor-not-allowed" disabled={docPage>=docTotalPages} onClick={()=> setDocPage(p=> Math.min(docTotalPages, p+1))}>ถัดไป</button>
              </div>
            </div>
          )}
          {/* ฟอร์มเพิ่ม/แก้ไขหนังสือราชการ */}
          {showDocForm && (
            <form onSubmit={handleDocFormSubmit} className="bg-muted/30 p-6 rounded mb-6 max-w-xl">
              <h3 className="font-semibold mb-4">{editingDoc ? 'แก้ไขหนังสือราชการ' : 'เพิ่มหนังสือราชการ'}</h3>
              <div className="mb-3">
                <label className="block mb-1">หัวข้อ</label>
                <input type="text" className="w-full border rounded px-3 py-2" value={docForm.title} onChange={e => setDocForm(f => ({ ...f, title: e.target.value }))} required />
              </div>
              <div className="mb-3">
                <label className="block mb-1">เลขที่หนังสือ</label>
                <input type="text" className="w-full border rounded px-3 py-2" value={docForm.document_number} onChange={e => setDocForm(f => ({ ...f, document_number: e.target.value }))} required />
              </div>
              <div className="mb-3">
                <label className="block mb-1">วันที่</label>
                <input type="date" className="w-full border rounded px-3 py-2" value={docForm.date} onChange={e => setDocForm(f => ({ ...f, date: e.target.value }))} required />
              </div>
              <div className="mb-3">
                <label className="block mb-1">หมวดหมู่</label>
                <select className="w-full border rounded px-3 py-2" value={docForm.category} onChange={e => setDocForm(f => ({ ...f, category: e.target.value }))} required>
                  <option value="">เลือกหมวดหมู่</option>
                  <option value="คำสั่ง">คำสั่ง</option>
                  <option value="ประกาศ">ประกาศ</option>
                  <option value="ระเบียบ">ระเบียบ</option>
                  <option value="หนังสือเวียน">หนังสือเวียน</option>
                  <option value="หนังสือแจ้ง">หนังสือแจ้ง</option>
                  <option value="หนังสือสั่งการ">หนังสือสั่งการ</option>
                  <option value="เรื่องที่เรียนรายทางเว็บไซต์">เรื่องที่เรียนรายทางเว็บไซต์</option>
                  <option value="งานประชุม">งานประชุม</option>
                  <option value="ข้อสั่งการที่ประชุม ปค.">ข้อสั่งการที่ประชุม ปค.</option>
                  <option value="อื่นๆ">อื่นๆ</option>
                </select>
              </div>
              <div className="mb-3">
                <label className="block mb-1">รายละเอียด</label>
                <textarea className="w-full border rounded px-3 py-2" rows={3} value={docForm.description} onChange={e => setDocForm(f => ({ ...f, description: e.target.value }))} />
              </div>
              <div className="mb-3">
                <label className="block mb-1">ไฟล์เอกสาร</label>
                <input type="file" onChange={e => setDocFile(e.target.files[0])} />
                {docForm.file_url && <a href={docForm.file_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline block mt-2">ดูไฟล์</a>}
              </div>
              <div className="flex gap-2 mt-4">
                <button type="submit" className="bg-primary text-white px-4 py-2 rounded" disabled={docFormLoading}>{docFormLoading ? 'กำลังบันทึก...' : 'บันทึก'}</button>
                <button type="button" className="bg-muted text-foreground px-4 py-2 rounded" onClick={() => { setShowDocForm(false); setEditingDoc(null); setDocForm({ title: '', document_number: '', date: '', category: '', file_url: '', description: '' }); setDocFile(null); }}>ยกเลิก</button>
              </div>
            </form>
          )}
        </div>
      )}
      {adminTab === 'procurement' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">จัดการจัดซื้อจัดจ้าง</h2>
            <button className="bg-primary text-white px-4 py-2 rounded" onClick={() => { setShowProcForm(true); setEditingProc(null); setProcForm({ title: '', summary: '', publish_date: '', category: 'จัดซื้อ', method: '', budget: '', file_url: '' }); setProcFile(null); }}>เพิ่มรายการ</button>
          </div>
          {procLoading ? <p>กำลังโหลด...</p> : procError ? <p className="text-red-600">{procError}</p> : (
            <table className="w-full text-sm mb-4">
              <thead>
                <tr className="bg-muted text-foreground">
                  <th className="py-2 px-2">หัวข้อ</th>
                  <th className="py-2 px-2">หมวด</th>
                  <th className="py-2 px-2">วิธี</th>
                  <th className="py-2 px-2">งบประมาณ</th>
                  <th className="py-2 px-2">วันที่</th>
                  <th className="py-2 px-2">ไฟล์</th>
                  <th className="py-2 px-2">จัดการ</th>
                </tr>
              </thead>
              <tbody>
                {procPaged.map((p) => (
                  <tr key={p.id} className="border-b">
                    <td className="py-2 px-2">{p.title}</td>
                    <td className="py-2 px-2">{p.category}</td>
                    <td className="py-2 px-2">{p.method}</td>
                    <td className="py-2 px-2">{p.budget ? Number(p.budget).toLocaleString('th-TH') : ''}</td>
                    <td className="py-2 px-2">{p.publish_date ? new Date(p.publish_date).toLocaleDateString('th-TH') : ''}</td>
                    <td className="py-2 px-2">{p.file_url && <a href={p.file_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">ดาวน์โหลด</a>}</td>
                    <td className="py-2 px-2">
                      <button className="text-blue-600 mr-2" onClick={() => { setEditingProc(p); setProcForm({ title: p.title || '', summary: p.summary || '', publish_date: p.publish_date?.slice(0,10) || '', category: p.category || 'จัดซื้อ', method: p.method || '', budget: p.budget || '', file_url: p.file_url || '' }); setProcFiles(Array.isArray(p.files) ? p.files.map(f => ({ name: f.name || '', url: f.url || '', file: null })) : []); setShowProcForm(true); }}>แก้ไข</button>
                      <button className="text-red-600" onClick={async () => { if (!window.confirm('ยืนยันลบรายการนี้?')) return; await supabase.from('procurements').delete().eq('id', p.id); loadProcList(); }}>ลบ</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!procLoading && !procError && (
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3 text-sm">
                <span>แสดงต่อหน้า:</span>
                <select aria-label="แสดงต่อหน้า" className="min-w-[84px] border border-border rounded-md px-3 py-1.5 bg-white text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary" value={procPageSize} onChange={(e)=> setProcPageSize(Number(e.target.value))}>
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                </select>
              </div>
              <div className="flex items-center gap-3">
                <button className="px-3 py-1.5 border border-border bg-muted rounded-md hover:bg-muted/70 disabled:opacity-40 disabled:cursor-not-allowed" disabled={procPage<=1} onClick={()=> setProcPage(p=> Math.max(1, p-1))}>ก่อนหน้า</button>
                <span className="text-sm">หน้า {procPage} / {procTotalPages}</span>
                <button className="px-3 py-1.5 border border-border bg-muted rounded-md hover:bg-muted/70 disabled:opacity-40 disabled:cursor-not-allowed" disabled={procPage>=procTotalPages} onClick={()=> setProcPage(p=> Math.min(procTotalPages, p+1))}>ถัดไป</button>
              </div>
            </div>
          )}

          {showProcForm && (
            <form onSubmit={async (e) => {
              e.preventDefault();
              setProcFormLoading(true);
              let fileUrl = procForm.file_url;
              if (procFile) {
                const fileExt = procFile.name.split('.').pop();
                const fileName = `${Date.now()}.${fileExt}`;
                const { error: uploadError } = await supabase.storage.from('proc-files').upload(fileName, procFile);
                if (uploadError) { setProcFormLoading(false); alert('อัปโหลดไฟล์ไม่สำเร็จ'); return; }
                fileUrl = supabase.storage.from('proc-files').getPublicUrl(fileName).data.publicUrl;
              }
              // อัปโหลดไฟล์แนบหลายไฟล์ (ถ้ามี)
              // รวมไฟล์เดิม (url) + ไฟล์ใหม่ (upload)
              const filesPayload = [];
              for (const item of (procFiles || [])) {
                if (item?.file) {
                  const ext = item.file.name.split('.').pop();
                  const fname = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
                  const { error: upErr } = await supabase.storage.from('proc-files').upload(fname, item.file);
                  if (upErr) { setProcFormLoading(false); alert('อัปโหลดไฟล์แนบไม่สำเร็จ'); return; }
                  const url = supabase.storage.from('proc-files').getPublicUrl(fname).data.publicUrl;
                  filesPayload.push({ name: item.name || item.file.name, url });
                } else if (item?.url) {
                  filesPayload.push({ name: item.name || 'ไฟล์แนบ', url: item.url });
                }
              }

              const payload = { ...procForm, budget: procForm.budget === '' ? null : Number(procForm.budget), file_url: fileUrl, files: filesPayload.length ? filesPayload : null };
              let resp;
              if (editingProc) resp = await supabase.from('procurements').update(payload).eq('id', editingProc.id);
              else resp = await supabase.from('procurements').insert([payload]);
              const { error } = resp;
              if (error) alert('บันทึกไม่สำเร็จ: ' + error.message);
              setShowProcForm(false);
              setEditingProc(null);
              setProcFile(null);
              setProcForm({ title: '', summary: '', publish_date: '', category: 'จัดซื้อ', method: '', budget: '', file_url: '' });
              setProcFiles([]);
              setProcFormLoading(false);
              loadProcList();
            }} className="bg-muted/30 p-6 rounded mb-6 max-w-xl">
              <h3 className="font-semibold mb-4">{editingProc ? 'แก้ไขรายการ' : 'เพิ่มรายการ'}</h3>
              <div className="mb-3">
                <label className="block mb-1">หัวข้อ</label>
                <input type="text" className="w-full border rounded px-3 py-2" value={procForm.title} onChange={e => setProcForm(f => ({ ...f, title: e.target.value }))} required />
              </div>
              <div className="mb-3">
                <label className="block mb-1">สรุป</label>
                <textarea className="w-full border rounded px-3 py-2" rows={3} value={procForm.summary} onChange={e => setProcForm(f => ({ ...f, summary: e.target.value }))} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block mb-1">วันที่ประกาศ</label>
                  <input type="date" className="w-full border rounded px-3 py-2" value={procForm.publish_date} onChange={e => setProcForm(f => ({ ...f, publish_date: e.target.value }))} required />
                </div>
                <div>
                  <label className="block mb-1">หมวด</label>
                  <select className="w-full border rounded px-3 py-2" value={procForm.category} onChange={e => setProcForm(f => ({ ...f, category: e.target.value }))}>
                    <option value="จัดซื้อ">จัดซื้อ</option>
                    <option value="จัดจ้าง">จัดจ้าง</option>
                    <option value="ประกาศ">ประกาศ</option>
                    <option value="แผน">แผน</option>
                    <option value="สัญญา">สัญญา</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="block mb-1">วิธีจัดซื้อจัดจ้าง</label>
                  <input type="text" className="w-full border rounded px-3 py-2" value={procForm.method} onChange={e => setProcForm(f => ({ ...f, method: e.target.value }))} />
                </div>
                <div>
                  <label className="block mb-1">งบประมาณ (บาท)</label>
                  <input type="number" step="0.01" className="w-full border rounded px-3 py-2" value={procForm.budget} onChange={e => setProcForm(f => ({ ...f, budget: e.target.value }))} />
                </div>
              </div>
              <div className="mt-3">
                <label className="block mb-1">ไฟล์เอกสาร (PDF)</label>
                <input type="file" onChange={e => setProcFile(e.target.files[0])} />
                {procForm.file_url && <a href={procForm.file_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline block mt-2">ดูไฟล์</a>}
              </div>
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="font-medium">ไฟล์แนบเพิ่มเติม</label>
                  <button type="button" className="text-sm bg-muted px-3 py-1 rounded" onClick={() => setProcFiles(arr => [...arr, { name: '', file: null, url: '' }])}>+ เพิ่มไฟล์แนบ</button>
                </div>
                <div className="space-y-3">
                  {(procFiles || []).map((it, idx) => (
                    <div key={idx} className="grid grid-cols-1 md:grid-cols-2 gap-3 items-center bg-muted/20 p-3 rounded">
                      <input type="text" placeholder="ชื่อไฟล์ที่จะแสดง (เช่น TOR, แบบฟอร์ม)" className="w-full border rounded px-3 py-2" value={it.name} onChange={e => setProcFiles(list => list.map((x,i)=> i===idx? { ...x, name: e.target.value }: x))} />
                      <div>
                        <input type="file" onChange={e => setProcFiles(list => list.map((x,i)=> i===idx? { ...x, file: e.target.files[0], url: '' }: x))} />
                        {it.url && <a href={it.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline ml-2 text-sm">ดูไฟล์เดิม</a>}
                      </div>
                      <div className="md:col-span-2 text-right">
                        <button type="button" className="text-red-600 text-sm" onClick={() => setProcFiles(list => list.filter((_,i)=> i!==idx))}>ลบ</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button type="submit" className="bg-primary text-white px-4 py-2 rounded" disabled={procFormLoading}>{procFormLoading ? 'กำลังบันทึก...' : 'บันทึก'}</button>
                <button type="button" className="bg-muted text-foreground px-4 py-2 rounded" onClick={() => { setShowProcForm(false); setEditingProc(null); }}>ยกเลิก</button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* แท็บการจองคิว */}
      {adminTab === 'appointments' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">จัดการการจองคิวออนไลน์</h2>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">ทั้งหมด</p>
                  <p className="text-2xl font-bold">{appointments.length}</p>
                </div>
                <Icon name="Calendar" size={24} className="text-primary" />
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">ยืนยันแล้ว</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {appointments.filter(a => a.status === 'confirmed').length}
                  </p>
                </div>
                <Icon name="CheckCircle" size={24} className="text-blue-600" />
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">ยกเลิก</p>
                  <p className="text-2xl font-bold text-red-600">
                    {appointments.filter(a => a.status === 'cancelled').length}
                  </p>
                </div>
                <Icon name="XCircle" size={24} className="text-red-600" />
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">เสร็จสิ้น</p>
                  <p className="text-2xl font-bold text-green-600">
                    {appointments.filter(a => a.status === 'completed').length}
                  </p>
                </div>
                <Icon name="CheckCircle2" size={24} className="text-green-600" />
              </div>
            </div>
          </div>

          {/* Filter */}
          <div className="mb-4">
            <div className="flex gap-2">
              <button
                onClick={() => setAppointmentStatusFilter('all')}
                className={`px-4 py-2 rounded ${appointmentStatusFilter === 'all' ? 'bg-primary text-white' : 'bg-muted text-foreground'}`}
              >
                ทั้งหมด
              </button>
              <button
                onClick={() => setAppointmentStatusFilter('confirmed')}
                className={`px-4 py-2 rounded ${appointmentStatusFilter === 'confirmed' ? 'bg-primary text-white' : 'bg-muted text-foreground'}`}
              >
                ยืนยันแล้ว
              </button>
              <button
                onClick={() => setAppointmentStatusFilter('cancelled')}
                className={`px-4 py-2 rounded ${appointmentStatusFilter === 'cancelled' ? 'bg-primary text-white' : 'bg-muted text-foreground'}`}
              >
                ยกเลิก
              </button>
              <button
                onClick={() => setAppointmentStatusFilter('completed')}
                className={`px-4 py-2 rounded ${appointmentStatusFilter === 'completed' ? 'bg-primary text-white' : 'bg-muted text-foreground'}`}
              >
                เสร็จสิ้น
              </button>
            </div>
          </div>

          {/* Appointments List */}
          {appointmentsLoading ? (
            <div className="text-center py-8">กำลังโหลด...</div>
          ) : appointmentsError ? (
            <div className="text-center py-8 text-red-600">เกิดข้อผิดพลาด: {appointmentsError}</div>
          ) : appointmentsPaged.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">ไม่พบการจองคิว</div>
          ) : (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">เลขที่จอง</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">ชื่อ-สกุล</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">โทรศัพท์</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">ประเภท</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">วันที่นัด</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">เวลา</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">สถานะ</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">จัดการ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {appointmentsPaged.map((apt) => (
                      <tr key={apt.id} className="border-t border-border hover:bg-muted/30">
                        <td className="px-4 py-3 text-sm">{apt.booking_number}</td>
                        <td className="px-4 py-3 text-sm">{apt.citizen_name}</td>
                        <td className="px-4 py-3 text-sm">{apt.phone_number}</td>
                        <td className="px-4 py-3 text-sm">
                          {apt.permit_type === 'firearm' ? 'อาวุธปืน' : 
                           apt.permit_type === 'hotel' ? 'โรงแรม' :
                           apt.permit_type === 'gambling' ? 'การพนัน' :
                           apt.permit_type === 'service' ? 'สถานบริการ' :
                           apt.permit_type === 'antique' ? 'ค้าของเก่า' : apt.permit_type}
                          {apt.firearm_sub_type && (
                            <div className="text-xs text-muted-foreground mt-1">
                              {apt.firearm_sub_type === 'p3_purchase_permit' ? 'ออกใบอนุญาตซื้อ ป.3' :
                               apt.firearm_sub_type === 'p4_possession_permit' ? 'ใบอนุญาต ป.4' :
                               apt.firearm_sub_type === 'p4_transfer_endorse' ? 'สลักหลังตัดโอน ป.4' :
                               apt.firearm_sub_type === 'other' ? 'อื่นๆ' : apt.firearm_sub_type}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {apt.appointment_date ? new Date(apt.appointment_date).toLocaleDateString('th-TH') : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm">{apt.time_slot || '-'}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 rounded text-xs ${
                            apt.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                            apt.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                            apt.status === 'completed' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {apt.status === 'confirmed' ? 'ยืนยันแล้ว' :
                             apt.status === 'cancelled' ? 'ยกเลิก' :
                             apt.status === 'completed' ? 'เสร็จสิ้น' : apt.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex flex-col gap-2">
                            <div className="flex gap-2">
                              <select
                                value={apt.status}
                                onChange={(e) => handleUpdateAppointmentStatus(apt.id, e.target.value)}
                                className="text-xs border rounded px-2 py-1"
                              >
                                <option value="confirmed">ยืนยันแล้ว</option>
                                <option value="cancelled">ยกเลิก</option>
                                <option value="completed">เสร็จสิ้น</option>
                              </select>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleOpenRescheduleModal(apt)}
                                className="text-blue-600 text-xs hover:underline px-2 py-1 border border-blue-300 rounded"
                                title="เลื่อนนัด"
                              >
                                เลื่อนนัด
                              </button>
                              <button
                                onClick={() => handleDeleteAppointment(apt.id)}
                                className="text-red-600 text-xs hover:underline px-2 py-1 border border-red-300 rounded"
                                title="ลบ"
                              >
                                ลบ
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {appointmentsTotalPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t border-border">
                  <div className="flex items-center gap-3 text-sm">
                    <span>แสดงต่อหน้า:</span>
                    <select
                      value={appointmentsPageSize}
                      onChange={(e) => setAppointmentsPageSize(Number(e.target.value))}
                      className="border rounded px-2 py-1"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setAppointmentsPage(p => Math.max(1, p - 1))}
                      disabled={appointmentsPage === 1}
                      className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      ก่อนหน้า
                    </button>
                    <span className="text-sm">
                      หน้า {appointmentsPage} / {appointmentsTotalPages}
                    </span>
                    <button
                      onClick={() => setAppointmentsPage(p => Math.min(appointmentsTotalPages, p + 1))}
                      disabled={appointmentsPage === appointmentsTotalPages}
                      className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      ถัดไป
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Reschedule Modal */}
      {showRescheduleModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg border border-border shadow-lg max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <Icon name="Calendar" size={20} color="white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">
                    เลื่อนนัดหมาย
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    เลขที่จอง: {selectedAppointment.booking_number}
                  </p>
                </div>
              </div>

              <form onSubmit={handleRescheduleAppointment} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    ชื่อ-สกุล
                  </label>
                  <input
                    type="text"
                    value={selectedAppointment.citizen_name}
                    disabled
                    className="w-full border border-border rounded-md px-3 py-2 bg-muted text-muted-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    วันที่นัดหมายเดิม
                  </label>
                  <input
                    type="text"
                    value={selectedAppointment.appointment_date ? new Date(selectedAppointment.appointment_date).toLocaleDateString('th-TH') : '-'}
                    disabled
                    className="w-full border border-border rounded-md px-3 py-2 bg-muted text-muted-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    เวลานัดหมายเดิม
                  </label>
                  <input
                    type="text"
                    value={selectedAppointment.time_slot || '-'}
                    disabled
                    className="w-full border border-border rounded-md px-3 py-2 bg-muted text-muted-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    วันที่นัดหมายใหม่ <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={rescheduleForm.appointment_date}
                    onChange={async (e) => {
                      const newDate = e.target.value;
                      setRescheduleForm({ ...rescheduleForm, appointment_date: newDate, time_slot: '' });
                      if (selectedAppointment) {
                        await fetchBookedSlots(newDate, selectedAppointment.id);
                      }
                    }}
                    min={new Date().toISOString().split('T')[0]}
                    required
                    className="w-full border border-border rounded-md px-3 py-2 bg-background text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    เวลานัดหมายใหม่ <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={rescheduleForm.time_slot}
                    onChange={(e) => setRescheduleForm({ ...rescheduleForm, time_slot: e.target.value })}
                    required
                    className="w-full border border-border rounded-md px-3 py-2 bg-background text-foreground"
                  >
                    <option value="">เลือกเวลา</option>
                    {timeSlots.map((slot) => {
                      const isBooked = bookedSlots.includes(slot.value);
                      
                      return (
                        <option 
                          key={slot.value} 
                          value={slot.value}
                          disabled={isBooked}
                          style={isBooked ? { color: '#999', backgroundColor: '#f5f5f5' } : {}}
                        >
                          {slot.label} {isBooked ? '(จองแล้ว)' : ''}
                        </option>
                      );
                    })}
                  </select>
                  {rescheduleForm.appointment_date && bookedSlots.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      มี {bookedSlots.length} ช่วงเวลาที่ถูกจองแล้วในวันนี้
                    </p>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={rescheduleLoading}
                    className="flex-1 bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {rescheduleLoading ? 'กำลังเลื่อนนัด...' : 'ยืนยันเลื่อนนัด'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowRescheduleModal(false);
                      setSelectedAppointment(null);
                      setRescheduleForm({ appointment_date: '', time_slot: '' });
                      setBookedSlots([]);
                    }}
                    className="flex-1 bg-muted text-foreground px-4 py-2 rounded-md hover:bg-muted/80"
                  >
                    ยกเลิก
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg border border-border shadow-lg max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="Edit" size={20} color="white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">
                    กรอกรายละเอียดการดำเนินการ
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {statusUpdateData.newStatus === 'in_progress' 
                      ? 'กรุณากรอกว่าดำเนินการถึงไหนแล้ว' 
                      : 'กรุณากรอกว่าดำเนินการเสร็จอย่างไร'}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    รายละเอียดการดำเนินการ
                    <span className="text-error ml-1">*</span>
                  </label>
                  <textarea
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none transition-colors resize-y min-h-[120px] focus:border-primary/60"
                    placeholder={
                      statusUpdateData.newStatus === 'in_progress'
                        ? 'เช่น กำลังตรวจสอบข้อมูล กำลังประสานงานกับหน่วยงานที่เกี่ยวข้อง กำลังรอเอกสารเพิ่มเติม ฯลฯ'
                        : 'เช่น ดำเนินการเสร็จสิ้นแล้ว ติดต่อผู้ยื่นเรื่องแจ้งผลแล้ว ส่งต่อหน่วยงานที่เกี่ยวข้องแล้ว ฯลฯ'
                    }
                    value={statusUpdateData.details}
                    onChange={(e) => setStatusUpdateData(prev => ({
                      ...prev,
                      details: e.target.value
                    }))}
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-border mt-6">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowStatusModal(false);
                    setStatusUpdateData({
                      complaintId: null,
                      newStatus: '',
                      details: ''
                    });
                  }}
                >
                  ยกเลิก
                </Button>
                <Button
                  onClick={handleStatusUpdate}
                  disabled={!statusUpdateData.details.trim()}
                >
                  <Icon name="Check" size={16} className="mr-2" />
                  บันทึก
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
